import React, { useState, useEffect, useMemo, useRef } from 'react';
import ApiClient from '../utils/ApiClient';
// Owner, 2026-09-07: the BuildPoModal enrichment points render here too, from the same endpoint
// with the same shared display rules, so the two purchasing surfaces can never drift.
import { dash, fmtPromise, fmtRank, fcPins, fetchEnrichment } from '../utils/poEnrichment';
import FcPinMap from '../components/Common/FcPinMap';
import toast from 'react-hot-toast';
// Task 8 (spec 2026-10-23 §9): the server decides each row's reasons and action bucket; these
// only group, count and render what it sent.
import ReasonChips from '../components/POCreation/ReasonChips';
import SkipModal from '../components/POCreation/SkipModal';
import SpreadSettingsModal from '../components/POCreation/SpreadSettingsModal';
import ReorderSettingsModal from '../components/POCreation/ReorderSettingsModal';
import { stockSpreadText, spreadCell, draftSpread, spreadBreakdown, spreadDetail, warningLevel, warningText, coverageBreakdown } from '../components/POCreation/spreadView';
import { BUCKETS, hasAdvice, viewBucket, bucketSummary, orderNowHandled, sortForBucket } from '../components/POCreation/reorderView';
// Task 3 (box configuration, mounted on the buying page): the SAME modal + behaviour hook the
// shipment planner uses -- one source of truth for the two-tier UNIT/CARTON packaging model,
// not a second implementation (228185e5's first-cut editor, superseded below). The pure helpers
// are imported straight from ShipmentPlanner.js rather than duplicated -- they are exported there
// specifically so a second host can consume them (isPackagingComplete was made exported for this).
import BoxConfigModal from '../components/BoxConfigModal';
import { useBoxConfig } from '../hooks/useBoxConfig';
import {
    isPackagingComplete, fmtLbsShort, computeLoadedBoxWeightLbs, deriveLegacyPackagingFields,
    computeShipmentTotals, boxFormFromCatalogBox, boxCatalogErrorText, unitBoxEntryFromDerived,
    unitBoxMatchesDerived, defaultPackagingStyle, packagingSummaryLine, packagingEntryFromRow,
    isAllowedModesSet, normalizeAllowedModes,
} from './ShipmentPlanner';

// Final review, Finding 3: the ceiling used to be pinned to this value unconditionally, so a
// legally LTL-designated ASIN's carton was rejected as "over the parcel ceiling" with no way to
// enter it from this page. It is now only the FALLBACK for when the ASIN's real allowed_modes
// designation is absent/blank (see shipmentModeForPackagingRow below) -- a purchasing-time default
// is far more likely to travel as a single small parcel than as LTL freight, so the stricter, more
// common ceiling is the honest default when nothing is known.
const PO_CREATION_DEFAULT_SHIPMENT_MODE = 'UPS';

// Final review, Finding 3 fix: derive the ASIN's real shipment-mode ceiling from the same GET
// /packaging row Finding 1 already fetches, instead of a page-wide constant. Doctrine holds here
// too -- unknown is never zero (or, here, never permissive): an absent/blank designation falls
// back to the strict UPS ceiling rather than assuming the ASIN can travel as freight.
//
// Owner decision (task 3): BOTH means EITHER mode is permitted -- it is NOT a statement that the
// product ships LTL. The old version of this function folded BOTH into the 'LTL' branch alongside
// an explicit LTL designation, silently granting the permissive 500 lb ceiling on the owner's
// behalf. BOTH now resolves to null ("no mode chosen yet") instead -- the modal shows a picker
// (see boxModalIsBothDesignated below) and the operator's pick becomes the real mode.
const packagingDesignationForRow = (row) => {
    if (!row || !isAllowedModesSet(row.allowedModes)) return null; // absent/blank -- the conservative default path, never a picker
    return normalizeAllowedModes(row.allowedModes); // 'UPS' | 'LTL' | 'BOTH'
};

const shipmentModeForDesignation = (designation) => {
    if (designation === 'BOTH') return null; // ambiguous -- needs an explicit operator pick
    return designation || PO_CREATION_DEFAULT_SHIPMENT_MODE; // 'UPS' | 'LTL' | absent-> UPS default
};

// useBoxConfig also takes four params that only mean something on a shipment plan: the PO-confirm
// step's confirmed/green-tick bookkeeping, and two planner maps describing rows already loaded
// onto a plan. None of that exists on this page -- stable no-op/empty stand-ins, defined once at
// module scope (never as fresh literals in the render body) so they can never look like a
// changed hook input on a re-render.
// Inbound cell hover: which report the number came from (server-side inboundSource).
const INBOUND_SOURCE_TITLE = {
    MANAGE_FBA_INVENTORY: 'from Manage FBA Inventory',
    RESTOCK_REPORT: 'from restock report',
};

const PO_CREATION_NOOP = () => {};
// Spec 2026-11-03 §6: units an overstock row ties up — FBA available plus inbound where present.
const overstockUnits = (s) => (Number(s.fbaAvailable) || 0) + (Number(s.fbaInbound) || 0);
// Owner 2026-11-03: units the price guard held back — the guard line carries the quantity it suppressed.
const heldBackUnits = (s) => Number((s.reasons || []).find(r => r.code === 'PRICE_LOSS_NO_ORDER')?.units) || 0;
const PO_CREATION_EMPTY_MAP = {};

// Owner 2026-09-09. Unknown is not zero: the previous comparator coerced null -> 0, so a missing
// Rank sorted as rank 0 (the best) and a missing COGS as free. Missing values now sort LAST
// regardless of direction — the direction ranks the values we actually have.
const isMissing = (v) => v == null || (typeof v === 'number' && Number.isNaN(v));

const compare = (av, bv, dir) => {
    const am = isMissing(av);
    const bm = isMissing(bv);
    if (am && bm) return 0;
    if (am) return 1;
    if (bm) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
        const cmp = String(av).localeCompare(String(bv));
        return dir === 'asc' ? cmp : -cmp;
    }
    return dir === 'asc' ? av - bv : bv - av;
};

// Task 6: the Status column now renders the computed, depth-only `stockStatus` (OUT/LOW/OK/null)
// rather than Amazon's old restock-report `alert`. Unknown returns null (not 0) so the shared
// `compare()` missing-last logic sinks it in BOTH sort directions, exactly like every other
// nullable column — it must never read as "healthiest".
const stockStatusSeverity = (s) => {
    if (s.stockStatus === 'OUT') return 3;
    if (s.stockStatus === 'LOW') return 2;
    if (s.stockStatus === 'OK') return 1;
    return null;
};

// BB Holder (owner brief 2026-09-13, corrected same day): who holds the Buy Box RIGHT NOW —
// distinct from BB Win above, which is a 28-day HISTORICAL rate. bbHolderState is 'US' |
// 'COMPETITOR' | null (server, POCreationService). Correction to the original brief: a null
// bb_seller_id on the source snapshot does NOT mean "nobody holds the Buy Box" — Amazon simply
// did not disclose the holder, so there is no third "no holder" state here, only UNKNOWN (see
// bbHolderCellContent's comment for the full trace). Sort text mirrors the cell's display text so
// sorting and reading can never disagree; null sinks last via the shared missing-last `compare()`
// below, exactly like every other nullable column.
// Owner 2026-10-12: the BB Holder filter groups rows by holder: 'US', the competitor's seller id
// (never its name -- names can repeat or change), or 'UNKNOWN' for a dash.
export const bbHolderKey = (s) => s.bbHolderState === 'US' ? 'US'
    : s.bbHolderState === 'COMPETITOR' ? (s.bbHolderId || s.bbHolderName || 'UNKNOWN') : 'UNKNOWN';

const bbHolderSortText = (s) => {
    if (s.bbHolderState === 'US') return 'You hold it';
    if (s.bbHolderState === 'COMPETITOR') return s.bbHolderName;
    return null;
};

// Reviewer, final fix wave (Important 1): the operator sees whatever is typed into the row's qty
// input, falling back to the suggestion when untouched. Est. Cost is derived from that SAME
// effective quantity. One helper, used by both the display cell and the sort accessor below, so
// the displayed number and the sorted number can never diverge again.
const effectiveQty = (item, selectedItems) =>
    selectedItems[item.asin]?.quantity ?? item.suggestedOrderQty;

const effectiveCost = (item, selectedItems) => {
    const qty = effectiveQty(item, selectedItems);
    return qty != null && item.priceListCost != null ? qty * item.priceListCost : null;
};

// Only columns whose sort key is NOT a flat row field. Everything else falls through to s[field].
// mpVel7d, localSfpUnits and gvs1Day (and the removed coverageGap) read the enrichment envelope (the `e`
// argument), which only ever covers rankFiltered.slice(0, 500) (see the enrichment-fetch effect
// below) — so with the rank cutoff blanked, sorting by any of these four columns ranks the first
// 500 rows and sinks the rest as missing. This is the same 500-row cap the branch deliberately
// removed for cheatedDays30, which is why that column reads the row (s.cheatedDays30, via the
// s[field] fallback) instead of the envelope. Known limitation, not a bug to rediscover.
//
// suggestedOrderQty and estimatedCost read `selectedItems` (the third arg, threaded through from
// the filter memo below) rather than the raw row field — a typed quantity must outrank the static
// server-suggested number in both cells, exactly as it outranks it in the cells themselves.
const SORT_ACCESSORS = {
    stockStatus: (s) => stockStatusSeverity(s),
    bbHolder: (s) => bbHolderSortText(s),
    gvs1Day: (s, e) => e?.gvs1Day,
    mpVel7d: (s, e) => e?.mpVel7d,
    localSfpUnits: (s, e) => e?.localSfpUnits,
    spread: (s) => spreadCell(s.spread).sort,
    suggestedOrderQty: (s, e, selectedItems) => effectiveQty(s, selectedItems),
    estimatedCost: (s, e, selectedItems) => effectiveCost(s, selectedItems),
};

// Lower rank is better, so Rank is the one column whose first click ascends.
const SORT_DEFAULT_DIR = { salesRank: 'asc', orderBy: 'asc' };

// Task 3. Owner: "the status column the user should be able to pick from the different status" —
// four independent checkboxes over the computed, depth-only stockStatus. "Needs Restock" is
// deliberately absent: it duplicated the separate "Only show items needing restock" toggle. None
// checked reads as "all" so the control states what it's doing rather than silently showing an
// empty catalog. Moved from the filter bar into the Status header's autofilter menu (owner,
// verbatim: "the status coulmn selectors should actually be shown when the 'status' column header
// is clicked. not as seperate check boxes").
const STATUS_OPTIONS = [
    { value: 'OUT', testId: 'status-out', label: 'Out' },
    { value: 'LOW', testId: 'status-low', label: 'Low' },
    { value: 'OK', testId: 'status-ok', label: 'OK' },
    // Important fix (3): Unknown is the least self-evident control on this menu and was the only
    // one with no tooltip. It hides TWO conditions with opposite remedies
    // (POCreationService.stockStatus): fbaAvailable == null (stock unmeasured today — fix
    // ingestion) or fbaAvailable > 0 with daysOfSupply == null (in stock, nothing sold in 30 days
    // — consider liquidating). In the default 500k view, every Unknown row measured is the second
    // kind.
    { value: 'UNKNOWN', testId: 'status-unknown', label: 'Unknown',
      title: 'Stock unmeasured today (absent from the AFN inventory batch), OR in stock but with zero sales in the last 30 days — two different problems with opposite remedies: fix ingestion for the first, consider liquidating for the second.' },
];

// Supplier picker sentinels. ALL = no constraint; NONE = the rows whose supplier column is null,
// which need a real option of their own or they would be unreachable from the picker entirely.
const ALL_SUPPLIERS = 'ALL';
const NO_SUPPLIER = 'NONE';

const sortValue = (s, field, enrichment, selectedItems) => {
    const accessor = SORT_ACCESSORS[field];
    return accessor ? accessor(s, enrichment[s.asin], selectedItems) : s[field];
};

export default function POCreation() {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    // Task 4: the ≤1-day FC coverage target driving Tasks 1-3's coverage-bound suggestions.
    // Server default is 80 (owner ruling); the cover curve flattens past 12 FCs (13-17 each add
    // roughly one metro), so a caution renders above 95 rather than letting the dial silently
    // roughly double the FC count for a few points of reach.
    const [coverageTargetPct, setCoverageTargetPct] = useState(80);
    const [selectedItems, setSelectedItems] = useState({});
    const [createdPo, setCreatedPo] = useState(null);   // last PO created this visit, for the Planner V2 link
    const [poNumber, setPoNumber] = useState('');
    const [creating, setCreating] = useState(false);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('unitsSold30d');
    const [sortDir, setSortDir] = useState('desc');
    // Task 3. Multi-select over the four real states of the computed, depth-only stockStatus.
    // 'UNKNOWN' stands for stockStatus === null — rows the page cannot assess, which it could
    // previously count but never list. Default all four: equivalent to the old "All Products".
    const [statusFilter, setStatusFilter] = useState(['OUT', 'LOW', 'OK', 'UNKNOWN']);
    // Owner: "the status coulmn selectors should actually be shown when the 'status' column header
    // is clicked. not as seperate check boxes" — the four checkboxes above move into a menu
    // anchored to the Status header (spreadsheet-autofilter pattern: sort AND filter together, per
    // owner decision, so sorting by status is never lost). Starts closed.
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const statusMenuBoxRef = useRef(null);
    const [bbFilter, setBbFilter] = useState([]);          // empty = every holder
    const [bbMenuOpen, setBbMenuOpen] = useState(false);
    const [bbMenuSearch, setBbMenuSearch] = useState('');
    const bbMenuBoxRef = useRef(null);
    // Task 3. Blank = off. Kept as a string so a half-typed "1" does not filter at 1%.
    const [marginMinPct, setMarginMinPct] = useState('');
    const [showOnlyNeedRestock, setShowOnlyNeedRestock] = useState(false);
    // Owner ruling 2026-09-08: "every asin to appear and a default filter to sales rank of 500k
    // or less". The server sends the whole catalog; this cutoff (PlanningTable semantics) is the
    // everyday view — blank shows everything INCLUDING unranked rows.
    // Owner 2026-09-11: opens scoped to Schluter — the reorder list starts on the vendor it is
    // for, with every other supplier one pick away. See effectiveSupplier for the no-match guard.
    const [supplierFilter, setSupplierFilter] = useState('schluter');
    const [rankCutoff, setRankCutoff] = useState('500000');
    // Owner 2026-09-09: "add a button to exclude product cheated on 50% or more - the 50% number
    // should be editable". PURCHASING filter only — cheated-day orders remain real demand and are
    // never filtered out of the demand map (ruling 2026-08-15). String like rankCutoff so the box
    // can be blanked to disable.
    const [excludeCheated, setExcludeCheated] = useState(false);
    const [cheatThresholdPct, setCheatThresholdPct] = useState('50');
    // Enrichment envelope (asin → data points) + which row's FC map is open on hover.
    const [enrichment, setEnrichment] = useState({});
    const [hoverAsin, setHoverAsin] = useState(null);
    // Task 3b: ad-hoc scorecard refresh, disabled while in flight so it cannot be double-fired.
    const [refreshingCoverage, setRefreshingCoverage] = useState(false);
    // Box configuration modal (Task 3): the shared BoxConfigModal + useBoxConfig, the SAME modal
    // and behaviour the shipment planner uses. This page keeps only its OWN small working map
    // (the row currently being edited, seeded from the suggestion row's known fields) -- there is
    // no plan, no PO-confirm step, and no shipment quantity here (see openBoxModal below).
    const [poPackagingByAsin, setPoPackagingByAsin] = useState({});
    const boxModalWrapperRef = useRef(null);
    // Finding B fix (final review): the save-error banner is rendered as a sibling of the modal's
    // protected panel (BoxConfigModal has no slot for a save failure that isn't box-catalog CRUD)
    // -- this ref lets the outside-click handler below protect it too, so clicking it can never be
    // mistaken for "dismiss this" and discard the values the failed save deliberately kept.
    const boxSaveErrorRef = useRef(null);
    // Finding 1/2/3 fix (final review): openBoxModal fetches the ASIN's real packaging row before
    // opening -- this is that fetch's in-flight/failure state. Null when no fetch is in flight.
    const [boxModalLoadingAsin, setBoxModalLoadingAsin] = useState(null);
    // Finding 3 fix: the shipment-mode ceiling for whichever ASIN's editor is currently open,
    // derived from that ASIN's real allowed_modes designation (see packagingDesignationForRow /
    // shipmentModeForDesignation). Defaults to the strict/common case; openBoxModal sets it
    // per-ASIN before opening. Owner decision (task 3): null means "a BOTH-designated ASIN with no
    // operator pick yet" -- useBoxConfig treats null as "assume no ceiling, block the form" rather
    // than ever guessing UPS or LTL on the operator's behalf.
    const [boxModalShipmentMode, setBoxModalShipmentMode] = useState(PO_CREATION_DEFAULT_SHIPMENT_MODE);
    // Owner decision (task 3): whether the ASIN currently open is BOTH-designated -- drives the
    // picker's VISIBILITY, which must persist across the operator switching UPS<->LTL (unlike
    // boxModalShipmentMode itself, which becomes a concrete 'UPS'/'LTL' the moment they pick).
    const [boxModalIsBothDesignated, setBoxModalIsBothDesignated] = useState(false);
    // Task 8: reorder view. All of it is inert unless the payload carries advice (hasAdvice) —
    // an old server or an advisor failure renders the page exactly as before.
    const [bucketTab, setBucketTab] = useState('ORDER_NOW');
    // Owner 2026-10-25: "have the columns open by default".
    const [showDetails, setShowDetails] = useState(true);
    const [reorderSettingsOpen, setReorderSettingsOpen] = useState(false);
    const [reorderSettings, setReorderSettings] = useState(null);
    const [boxMissingOnly, setBoxMissingOnly] = useState(false);
    // Spec 2026-11-03 §6: "Overstock — liquidate or recall" — rows the overstock guard held at 0, across all tabs.
    const [overstockOnly, setOverstockOnly] = useState(false);
    // Owner 2026-11-03 "zero until fixed": rows the price guard held at 0 (a seller below MAP / cheaper wins the box).
    const [priceLossOnly, setPriceLossOnly] = useState(false);
    const [skipRow, setSkipRow] = useState(null);
    const [spreadSettingsOpen, setSpreadSettingsOpen] = useState(false);
    const [spreadSummary, setSpreadSummary] = useState(null);
    // A column-header click overrides the bucket's own order (order-by, then dollars at risk).
    const [userSorted, setUserSorted] = useState(false);

    // Spec 2026-11-01 F1: cover at landing + lead legs live in the reorder settings, not page inputs.
    const loadReorderSettings = () => ApiClient.get('/api/po-creation/reorder-settings')
        .then(res => setReorderSettings(res && res.data && res.data.settings ? res.data.settings : null))
        .catch(() => setReorderSettings(null));

    useEffect(() => {
        fetchSuggestions();
        fetchNextPO();
        loadReorderSettings();
        // Daily spread strip: purely informational, so any failure just hides it.
        ApiClient.get('/api/po-creation/spread-summary')
            .then(res => setSpreadSummary(res && res.data && res.data.products ? res.data : null))
            .catch(() => setSpreadSummary(null));
    }, []);

    // Task 4: extended to carry the coverage target explicitly (covPct), the same reason
    // handleTargetDaysChange already passes `val` for days rather than trusting setTargetDays to
    // have landed by the time this runs. Review I1 (2026-09-12): rankCutoff now gets the same
    // treatment via `rc` — it used to be read straight from state with no handler ever firing a
    // refetch on its own change, so widening the cutoff showed new rows still carrying quantities
    // computed under the OLD gate until the operator happened to press Refresh.
    // Final review, Finding D: returns whether the refresh actually landed, so a caller that needs
    // to know (onSaved, below) can tell a genuine refresh from one that silently kept the old
    // table. `silentError` skips the generic toast for callers that show their own, more specific
    // message instead of (or in addition to) this one.
    const fetchSuggestions = async (covPct, rc, { silentError = false } = {}) => {
        setLoading(true);
        try {
            const c = (covPct === undefined || covPct === null || covPct === '') ? coverageTargetPct : covPct;
            const rankVal = (rc === undefined || rc === null) ? rankCutoff : rc;
            const rcTrimmed = rankVal.trim();
            // Judgement call: blank rankCutoff already means "no display cutoff" for this page's
            // own table filter, but the SAME field also gates which ASINs the server will produce
            // a coverage-driven quantity for. Sending 0 would qualify NO asin (silently disabling
            // coverage-driven seeding entirely); sending an unbounded value would reopen the
            // $1.02M unconditional seeding exposure this plan measured (vs $89k gated at 500k).
            // So a blank box omits the param and lets the server's own default (500000) stand.
            const rankParam = rcTrimmed === '' ? '' : `&rankCutoff=${encodeURIComponent(rcTrimmed)}`;
            // 2026-10-25: a cold server load can take ~40 s; ApiClient's 30 s default dropped it and the
            // page showed no products. This one request gets 120 s.
            const res = await ApiClient.get(`/api/po-creation/suggestions?coverageTargetPct=${c}${rankParam}`,
                { timeout: 120000 });
            setSuggestions(res.data);
            return true;
        } catch (err) {
            if (!silentError) toast.error('Failed to load restock suggestions');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const fetchNextPO = async () => {
        try {
            const res = await ApiClient.get('/api/po-creation/next-po-number');
            setPoNumber(res.data.poNumber);
        } catch (err) {
            console.error('Failed to fetch next PO number');
        }
    };

    const handleCoverageTargetChange = (val) => {
        setCoverageTargetPct(val);
        fetchSuggestions(val);
    };

    // Review I1: mirrors handleCoverageTargetChange — passes the new value
    // explicitly rather than trusting setRankCutoff to have landed by the time fetchSuggestions
    // reads state, and refetches so a widened cutoff never shows rows still seeded under the OLD
    // gate.
    const handleRankCutoffChange = (val) => {
        setRankCutoff(val);
        fetchSuggestions(coverageTargetPct, val);
    };

    const toggleItem = (asin) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[asin]) {
                delete next[asin];
            } else {
                const item = suggestions.find(s => s.asin === asin);
                if (item) {
                    next[asin] = {
                        asin: item.asin,
                        sku: item.sku,
                        supplierSku: item.supplierSku,
                        productName: item.productName,
                        quantity: item.suggestedOrderQty || 1,
                        sfpQuantity: 0,
                        spreadQuantity: draftSpread(item, item.suggestedOrderQty || 1, 0),
                        spreadDetail: spreadDetail(item.spread),
                        unitCost: item.priceListCost   // single source of truth (owner 2026-09-09)
                    };
                }
            }
            return next;
        });
    };

    // Owner 2026-09-09: typing a quantity selects the row. That means this can run for a row with
    // NO existing draft entry, so it must seed the whole item — the old `{ ...prev[asin] }` spread
    // produced `{ quantity }` with no asin, sku, productName or unitCost for an unticked row, and
    // that partial line would pass the quantity>0 filter into createPurchaseOrder.
    const updateDraftQty = (asin, qty) => {
        const n = Math.max(0, parseInt(qty) || 0);
        setSelectedItems(prev => {
            const next = { ...prev };
            if (n === 0) { delete next[asin]; return next; }
            const item = suggestions.find(s => s.asin === asin);
            // Defensive guard (not reachable today — asin always comes from a rendered row, so it
            // always matches something in `suggestions`): without this, an unmatched asin would
            // seed every field as undefined via the optional chaining below, recreating the
            // partial-draft-line bug this file already fixed once (see toggleItem's comment).
            if (!item) return prev;
            const base = prev[asin] || {
                asin: item?.asin,
                sku: item?.sku,
                supplierSku: item?.supplierSku,
                productName: item?.productName,
                unitCost: item?.priceListCost,
                sfpQuantity: 0,
                spreadQuantity: draftSpread(item, n, 0),
                spreadDetail: spreadDetail(item.spread),
            };
            const sfpN = Math.min(base.sfpQuantity || 0, n);
            next[asin] = { ...base, quantity: n, sfpQuantity: sfpN,
                spreadQuantity: Math.min(base.spreadQuantity || 0, Math.max(0, n - sfpN)) };
            return next;
        });
    };

    // PO SFP split (spec 2026-10-10): units of a draft line kept in the warehouse for SFP;
    // the rest go to FBA. Clamped to 0..quantity on a line already in the draft. Owner 2026-10-12:
    // typing SFP on a row not yet in the draft adds it (as typing a quantity does), ordering the
    // suggested quantity or the SFP units, whichever is more; a blank or 0 adds nothing.
    const updateDraftSfp = (asin, value) => {
        const typed = Math.max(0, parseInt(value) || 0);
        setSelectedItems(prev => {
            if (prev[asin]) {
                const n = Math.min(typed, prev[asin].quantity || 0);
                return { ...prev, [asin]: { ...prev[asin], sfpQuantity: n,
                    spreadQuantity: Math.min(prev[asin].spreadQuantity || 0, Math.max(0, (prev[asin].quantity || 0) - n)) } };
            }
            if (typed === 0) return prev;
            const item = suggestions.find(s => s.asin === asin);
            if (!item) return prev;
            const qty = Math.max(item.suggestedOrderQty || 0, typed);
            return {
                ...prev,
                [asin]: {
                    asin: item.asin,
                    sku: item.sku,
                    supplierSku: item.supplierSku,
                    productName: item.productName,
                    unitCost: item.priceListCost,
                    quantity: qty,
                    sfpQuantity: typed,
                    spreadQuantity: draftSpread(item, qty, typed),
                    spreadDetail: spreadDetail(item.spread),
                },
            };
        });
    };

    // FC spread units of a draft line (spec 2026-10-28): the part of the quantity meant to seed
    // missing FC regions. Editable, clamped to 0..(quantity - SFP).
    const updateDraftSpread = (asin, value) => {
        const typed = Math.max(0, parseInt(value) || 0);
        setSelectedItems(prev => {
            if (!prev[asin]) return prev;
            const room = Math.max(0, (prev[asin].quantity || 0) - (prev[asin].sfpQuantity || 0));
            return { ...prev, [asin]: { ...prev[asin], spreadQuantity: Math.min(typed, room) } };
        });
    };

    const removeFromDraft = (asin) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            delete next[asin];
            return next;
        });
    };

    const selectAllNeedRestock = () => {
        const newSelected = { ...selectedItems };
        filteredSuggestions.forEach(item => {
            if (item.suggestedOrderQty > 0 && !newSelected[item.asin]) {
                newSelected[item.asin] = {
                    asin: item.asin,
                    sku: item.sku,
                    supplierSku: item.supplierSku,
                    productName: item.productName,
                    quantity: item.suggestedOrderQty,
                    sfpQuantity: 0,
                    spreadQuantity: draftSpread(item, item.suggestedOrderQty, 0),
                    spreadDetail: spreadDetail(item.spread),
                    unitCost: item.priceListCost   // single source of truth (owner 2026-09-09)
                };
            }
        });
        setSelectedItems(newSelected);
    };

    const clearDraft = () => setSelectedItems({});

    // Task 8: every Order-now row that is not skipped, has a suggestion, and is not already in
    // the draft goes through the same per-row add path as ticking its checkbox.
    const addAllOrderNow = () => {
        visibleOrderNow
            .filter(s => !s.skip && s.suggestedOrderQty > 0 && !selectedItems[s.asin])
            .forEach(s => toggleItem(s.asin));
    };

    const unskip = async (asin) => {
        try {
            await ApiClient.delete('/api/po-creation/skip/' + asin);
            fetchSuggestions();
        } catch (err) {
            toast.error('Could not un-skip ' + asin);
        }
    };

    const handleCreatePO = async () => {
        const items = Object.values(selectedItems).filter(i => i.quantity > 0);
        if (items.length === 0) {
            toast.error('No items in PO draft');
            return;
        }
        setCreating(true);
        try {
            const res = await ApiClient.post('/api/po-creation/create', {
                poNumber,
                items
            });
            const createdMsg = `PO #${res.data.poNumber} created: ${res.data.totalItems} items, ${res.data.totalQuantity} units`
                + `${res.data.totalSfp > 0 ? ` (${res.data.totalSfp} for SFP)` : ''}, $${res.data.totalCost.toLocaleString()}`;
            toast.success(createdMsg);
            setCreatedPo(res.data.poNumber);
            setSelectedItems({});
            fetchNextPO();
            fetchSuggestions();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to create PO');
        } finally {
            setCreating(false);
        }
    };

    // Task 3b: POST /api/war-room/refresh already exists (ScorecardController) and is the same
    // recompute ScorecardJobs runs nightly at 06:00 ET. This is wiring, not new machinery. On
    // failure the table is left as-is — a failed refresh must not clear what's already shown.
    const handleRefreshCoverage = async () => {
        setRefreshingCoverage(true);
        try {
            await ApiClient.post('/api/war-room/refresh');
            await fetchSuggestions();
        } catch (err) {
            toast.error('Failed to refresh coverage');
        } finally {
            setRefreshingCoverage(false);
        }
    };

    const handleSort = (field) => {
        setUserSorted(true);
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir(SORT_DEFAULT_DIR[field] || 'desc');
        }
    };

    // The status header menu's two sort entries set direction explicitly (clicking "Out first"
    // twice must keep Out first, unlike handleSort's own toggle) — but they still go through the
    // exact same sortField/sortDir state handleSort uses, so there is only ever one sort path.
    const sortStockStatus = (dir) => {
        setUserSorted(true);
        setSortField('stockStatus');
        setSortDir(dir);
    };

    // Dismissal pattern copied from PrintLabelsAction.js (useRef + mousedown-while-open listener,
    // removed on cleanup) — plus Escape, which that file doesn't handle. A filter menu that hides
    // rows and has no keyboard escape is a trap.
    useEffect(() => {
        if (!statusMenuOpen) return undefined;
        const onMouseDown = (e) => {
            if (statusMenuBoxRef.current && !statusMenuBoxRef.current.contains(e.target)) {
                setStatusMenuOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setStatusMenuOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [statusMenuOpen]);

    useEffect(() => {
        if (!bbMenuOpen) return undefined;
        const onMouseDown = (e) => {
            if (bbMenuBoxRef.current && !bbMenuBoxRef.current.contains(e.target)) setBbMenuOpen(false);
        };
        const onKeyDown = (e) => { if (e.key === 'Escape') setBbMenuOpen(false); };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [bbMenuOpen]);

    const {
        packagingPopoverAsin, packagingPopoverTitle, packagingPopoverQty,
        packagingForm, packagingFieldErrors, packagingFormValid, packagingAttemptedSubmit,
        packagingStyleChoice, packagingSaving, packagingLoadedLbs, saveError,
        packagingModalActivePkg, packagingModalDerived, packagingModalLegacyOnly, packagingModalTotals,
        boxCatalog, choosePackagingStyle, closePackagingPopover, applyPackaging, updatePackagingField,
        newBoxOpen, newBoxForm, newBoxSaving, editingBoxId, boxError, boxNote, selectedBox,
        updateNewBoxField, saveNewBoxToCatalog, renderCatalogBoxCard,
        setEditingBoxId, setNewBoxForm, setBoxError, setNewBoxOpen, setSelectedBoxId,
        openPackagingPopover,
    } = useBoxConfig({
        // Finding 3 fix: per-ASIN, derived from that ASIN's real allowed_modes designation --
        // never the page-wide constant.
        shipmentMode: boxModalShipmentMode,
        packagingByAsin: poPackagingByAsin,
        poConfirmDefaultsByAsin: PO_CREATION_EMPTY_MAP,
        poPackagingMetaByAsin: PO_CREATION_EMPTY_MAP,
        setPackagingByAsin: setPoPackagingByAsin,
        // requirePersist below makes the hook await the POST before ever applying/closing, so any
        // setToast call here is a genuine client-side validation refusal (missing weight, over
        // the parcel ceiling, etc.) -- exactly the same kind of message the planner shows via its
        // own toast. A "make default" POST failure never reaches this callback; it surfaces as
        // `saveError` instead (rendered as the box-save-error banner below).
        setToast: (message) => toast.error(message),
        setPoConfirmConfirmed: PO_CREATION_NOOP,
        setPackagingConfirmedAsin: PO_CREATION_NOOP,
        isPackagingComplete, fmtLbsShort, computeLoadedBoxWeightLbs, deriveLegacyPackagingFields,
        computeShipmentTotals, boxFormFromCatalogBox, boxCatalogErrorText, unitBoxEntryFromDerived,
        unitBoxMatchesDerived, defaultPackagingStyle,
        // Finding 1 fix (task-3 report): this page has no shipment for a config to be "applied"
        // to -- the "make default" POST IS the entire operation. requirePersist:true makes the
        // hook await that POST BEFORE applying/closing/firing onSaved, so by the time onSaved
        // runs here the save has genuinely succeeded -- safe to refetch immediately instead of
        // needing to sequence it through a page-level wrapper.
        //
        // Finding D fix (final review): the refetch used to be fire-and-forget -- a POST that
        // succeeded followed by a refetch that failed left the table showing the pre-save pack
        // with only fetchSuggestions' own generic "Failed to load restock suggestions" toast,
        // which says nothing about the save that just happened. silentError suppresses that
        // generic toast here so this one, more specific message is the only one the operator
        // sees: the save is never rolled back, but the figures on screen may now be stale.
        onSaved: async () => {
            const refreshed = await fetchSuggestions(undefined, undefined, { silentError: true });
            if (!refreshed) {
                toast.error('Pack saved, but the table failed to refresh — the numbers shown may be stale until you hit Refresh.');
            }
        },
        requirePersist: true,
    });

    // Opens the shared box-configuration editor for a row. Finding 1/2/3 fix (final review): this
    // used to seed the hook's own working map straight from the SUGGESTION row -- but
    // POCreationDTO.Suggestion can only ever carry unitsPerBox + the four legacy box_* columns, so
    // a catalog-linked config (unit weight + shared box already on file) opened blank and
    // mislabeled "legacy", the Amazon unit-box suggestion never reached this page, and the parcel
    // ceiling was pinned to UPS regardless of the ASIN's real allowed_modes designation.
    //
    // This now fetches the ASIN's real packaging row first -- the SAME GET the shipment planner
    // reads, parsed by the SAME helper (packagingEntryFromRow) -- so the editor is seeded from the
    // truth instead of a lossy echo of it. Handled honestly: the box cell shows a loading state
    // while the fetch is in flight (see boxModalLoadingAsin below), and a failed fetch never opens
    // the editor at all -- doctrine (unknown is never zero) forbids silently opening a blank form
    // that could overwrite a good saved config, so the destructive path stays closed and the
    // operator is told why instead.
    //
    // No shipment context exists on this page -- the row's suggestedOrderQty is a PURCHASE
    // quantity, not a SHIPMENT quantity, and reusing it here would fabricate a "units -> boxes ->
    // total weight" line for a shipment that does not exist. qty is deliberately null; the modal's
    // totals line does not render without one (see computeShipmentTotals / packagingModalTotals).
    //
    // openPackagingPopover seeds the form by reading packagingByAsin[asin] AT CALL TIME -- but
    // setPoPackagingByAsin above is a scheduled React state update, not an immediate write, so
    // calling openPackagingPopover synchronously in the same tick would still see the OLD map and
    // seed blank. pendingBoxOpen defers the actual open to the effect below, which runs after the
    // seed has committed and a fresh openPackagingPopover (closing over the updated map) exists.
    const [pendingBoxOpen, setPendingBoxOpen] = useState(null);

    const openBoxModal = async (item) => {
        setBoxModalLoadingAsin(item.asin);
        try {
            const res = await ApiClient.get(`/api/shipment-planner/packaging?asins=${encodeURIComponent(item.asin)}`);
            const row = (res.data || []).find(r => r.asin === item.asin) || null;
            setPoPackagingByAsin(prev => ({ ...prev, [item.asin]: packagingEntryFromRow(row) }));
            const designation = packagingDesignationForRow(row);
            setBoxModalIsBothDesignated(designation === 'BOTH');
            setBoxModalShipmentMode(shipmentModeForDesignation(designation));
            setPendingBoxOpen({ asin: item.asin, title: item.productName });
        } catch (err) {
            console.error('Failed to load packaging for box editor:', err);
            toast.error(`Could not load the current packaging for ${item.asin} — try again.`);
        } finally {
            setBoxModalLoadingAsin(null);
        }
    };

    useEffect(() => {
        if (!pendingBoxOpen) return;
        openPackagingPopover(pendingBoxOpen.asin, pendingBoxOpen.title, null);
        setPendingBoxOpen(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingBoxOpen]);

    // PO Creation has no "apply to a shipment, and maybe also save it as the default" distinction
    // -- there is no shipment here, so the only thing an apply can mean is the persisted default.
    // BoxConfigModal is mounted below with primaryActionOnly, so it renders only the one button
    // that persists ("Use and make default for {ASIN}"), wired straight to the hook's own
    // applyPackaging(true) -- no page-level wrapper needed. requirePersist:true (passed into
    // useBoxConfig above) makes the hook await that POST before applying/closing/firing onSaved,
    // so a failed save leaves the editor open with `saveError` set (rendered as the banner below)
    // instead of reporting success for something that never happened.
    //
    // Finding C/5 fix (final review): BoxConfigModal now carries a stable data-testid on its panel
    // (data-testid="box-config-panel") -- outside-click detection locates it explicitly instead of
    // by fixed DOM position (the old ':scope > div > div', which silently broke -- backdrop clicks
    // stop closing the modal, no error -- if the component ever grew a wrapper element). A click
    // landing outside that panel -- the backdrop, or anywhere else in the document -- dismisses
    // without saving, same as Cancel/X.
    //
    // Finding B fix: the save-error banner is rendered as a sibling of the panel (BoxConfigModal
    // has no slot for a failure that isn't box-catalog CRUD), so it is explicitly protected here
    // too -- clicking it must never be treated as "outside" and discard the values a failed save
    // deliberately kept. Same dismissal shape as the status menu above otherwise: useRef +
    // mousedown-while-open listener, removed on cleanup, plus Escape.
    useEffect(() => {
        if (!packagingPopoverAsin) return undefined;
        const onMouseDown = (e) => {
            const panel = boxModalWrapperRef.current?.querySelector('[data-testid="box-config-panel"]');
            const insidePanel = !!panel && panel.contains(e.target);
            const insideSaveError = !!boxSaveErrorRef.current && boxSaveErrorRef.current.contains(e.target);
            if (!insidePanel && !insideSaveError) closePackagingPopover();
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closePackagingPopover();
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [packagingPopoverAsin]);

    // Owner 2026-09-11: "we actually need a drop down that the 'Supplier' can be picked which
    // matches the supplied column". Options come from the data rather than a hardcoded list, so they
    // cannot drift as suppliers come and go. NO_SUPPLIER buckets the rows whose label is null — 70
    // catalog ASINs, of which 51 are vendor rows whose item codes are plainly Schluter (KERDI-LINE-A,
    // DILEX-STF) and 19 came from amazon_api/keepa. They get their own entry rather than being
    // unreachable.
    const supplierOptions = useMemo(() => {
        const named = [...new Set(suggestions.map(s => s.supplier).filter(Boolean))].sort();
        const hasUnlabelled = suggestions.some(s => !s.supplier);
        return [...named, ...(hasUnlabelled ? [NO_SUPPLIER] : [])];
    }, [suggestions]);

    // The default is a hardcoded string, so it can only be trusted while the catalog actually holds
    // a row labelled with it. If it does not — a renamed supplier, a changed case, an empty load —
    // fall back to ALL rather than presenting an empty table as though the catalog were empty. That
    // failure mode (a filter silently emptying the page) is the one this page keeps designing
    // against, and a hardcoded default is exactly how it would arrive.
    const effectiveSupplier = (supplierFilter === ALL_SUPPLIERS
        || supplierOptions.includes(supplierFilter)) ? supplierFilter : ALL_SUPPLIERS;

    // Supplier scopes the catalog BEFORE the rank slice, so everything downstream inherits it: the
    // cards, and the 500-ASIN enrichment cap, which would otherwise spend its budget on rows this
    // filter is about to discard.
    const supplierFiltered = useMemo(() => {
        if (effectiveSupplier === ALL_SUPPLIERS) return suggestions;
        return suggestions.filter(s => (s.supplier || NO_SUPPLIER) === effectiveSupplier);
    }, [suggestions, effectiveSupplier]);

    // The rank slice, applied before the transient filters: with a cutoff, unranked rows and
    // over-cutoff rows hide (a rank the filter can't compare is not "under 500k"); blank = all.
    const rankFiltered = useMemo(() => {
        const cutoff = rankCutoff.trim() === '' ? null : Number(rankCutoff);
        if (cutoff === null || Number.isNaN(cutoff)) return supplierFiltered;
        return supplierFiltered.filter(s => s.salesRank != null && s.salesRank <= cutoff);
    }, [supplierFiltered, rankCutoff]);

    // One number drives BOTH the exclusion and the amber shading. 50% of the 30-day window = 15
    // days, and the test is >= (the owner said "50% or more"). null disables both.
    const cheatDayThreshold = useMemo(() => {
        const pct = Number(cheatThresholdPct);
        if (!cheatThresholdPct.trim() || Number.isNaN(pct) || pct <= 0) return null;
        return Math.ceil((pct / 100) * 30);
    }, [cheatThresholdPct]);

    // Enrichment is context, not a gate — fetched for the rank-filtered view only (the full
    // catalog is ~3k ASINs; the enrichment endpoint does per-ASIN work, so an uncapped fetch
    // would grind). Capped at 500 ASINs (default 500k view is ~200-400); rows beyond the cap and
    // fetch failures render dashes per the shared display rules. Debounced so typing in the
    // cutoff box doesn't fire a request per keystroke.
    useEffect(() => {
        const asins = rankFiltered.slice(0, 500).map(s => s.asin);
        if (asins.length === 0) { setEnrichment({}); return undefined; }
        const t = setTimeout(() => {
            fetchEnrichment(ApiClient, asins).then(setEnrichment);
        }, 600);
        return () => clearTimeout(t);
    }, [rankFiltered]);

    // Search + status only — split out from the rest of the pipeline so the margin threshold's own
    // exclusion count (Critical 2, below) can be measured against exactly the population that step
    // receives, without duplicating the filter logic a second time.
    const advice = useMemo(() => hasAdvice(suggestions), [suggestions]);
    const bucketCounts = useMemo(() => bucketSummary(rankFiltered), [rankFiltered]);
    // Detail columns hide behind Show details only when the payload carries advice.
    const D = (node) => (!advice || showDetails ? node : null);

    const searchStatusNoBb = useMemo(() => {
        let result = [...rankFiltered];

        if (search) {
            const q = search.toLowerCase();
            result = result.filter(s =>
                s.productName?.toLowerCase().includes(q) ||
                s.asin?.toLowerCase().includes(q) ||
                s.sku?.toLowerCase().includes(q)
            );
        }

        // Task 3. Reviewer, final-review fix wave (Critical 1) carried forward: this used to filter
        // on Amazon's deleted restock-report `alert` field — measured on prod, 111 of 161 "Out of
        // Stock" matches actually HOLD sellable New stock. It repoints at the computed
        // `stockStatus` (OUT/LOW/OK/null), the same field the Status column and its sort already
        // use. None checked is treated as no constraint, so an accidental empty selection cannot
        // present as an empty catalog — the control reads "all" in that state.
        if (statusFilter.length > 0 && statusFilter.length < 4) {
            result = result.filter(s => statusFilter.includes(s.stockStatus ?? 'UNKNOWN'));
        }

        return result;
    }, [rankFiltered, search, statusFilter]);

    const searchStatusFiltered = useMemo(() => (bbFilter.length > 0
        ? searchStatusNoBb.filter(s => bbFilter.includes(bbHolderKey(s)))
        : searchStatusNoBb), [searchStatusNoBb, bbFilter]);

    // The BB Holder menu's choices: every holder in the catalog with how many products it holds,
    // largest first; "You" first and "Unknown" last.
    const bbHolderOptions = useMemo(() => {
        const byKey = new Map();
        for (const s of suggestions) {
            const key = bbHolderKey(s);
            const label = key === 'US' ? 'You (Occam\'s Razor)' : key === 'UNKNOWN' ? 'Unknown (—)' : (s.bbHolderName || key);
            const o = byKey.get(key) || { key, label, count: 0 };
            o.count += 1;
            byKey.set(key, o);
        }
        const rank = (o) => o.key === 'US' ? 0 : o.key === 'UNKNOWN' ? 2 : 1;
        return [...byKey.values()].sort((a, b) => rank(a) - rank(b) || b.count - a.count || a.label.localeCompare(b.label));
    }, [suggestions]);

    // Critical fix (C2): only 216 of 3,002 catalog ASINs can have a non-null grossMarginPct at all
    // (147 of 215 in the default 500k view). Excluding unknown-percent rows from the margin filter
    // is correct — an unknown percent cannot satisfy "> X" — but silence about HOW MANY rows that
    // drops is not: a blanked rank cutoff plus "Margin > 10" looks identical whether it dropped
    // genuinely-thin-margin rows or 2,786 unmeasurable ones. Measured over the SAME population the
    // margin step itself filters (post search/status, pre margin), so this states exactly what
    // THIS control is discarding, not some other slice of the page.
    const marginExcludedCount = useMemo(() => {
        if (marginMinPct.trim() === '' || Number.isNaN(parseFloat(marginMinPct))) return 0;
        return searchStatusFiltered.filter(s => s.grossMarginPct == null).length;
    }, [searchStatusFiltered, marginMinPct]);

    // Margin, need-restock and cheat filters — one function so the visible pipeline and the
    // hidden-filter chip (Task 8 review I1) cannot drift apart.
    const applyRestFilters = (rows) => {
        let result = [...rows];

        // Task 3. The ONE place on this page where unknown is filtered out: an unknown margin
        // cannot be shown to satisfy "> X". Written explicitly — `null > 15` is false in JS, so
        // the naive form would be accidentally right today and silently wrong after the next edit.
        const minPct = parseFloat(marginMinPct);
        if (!Number.isNaN(minPct)) {
            result = result.filter(s => s.grossMarginPct != null && s.grossMarginPct > minPct);
        }

        if (showOnlyNeedRestock) {
            result = result.filter(s => s.suggestedOrderQty > 0);
        }

        if (excludeCheated && cheatDayThreshold != null) {
            // Unknown is never excluded: a payload regression must not silently drop products from
            // the buy list without saying so.
            result = result.filter(s => s.cheatedDays30 == null
                || s.cheatedDays30 < cheatDayThreshold);
        }
        return result;
    };

    // Every row the purchaser can see across all tabs (every filter except the bucket tab).
    const preBucket = useMemo(() => applyRestFilters(searchStatusFiltered),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchStatusFiltered, marginMinPct, showOnlyNeedRestock, excludeCheated, cheatDayThreshold]);

    const filteredSuggestions = useMemo(() => {
        let result = [...preBucket];

        // Spec 2026-11-03 §6: overstock rows from every bucket, most dollars at cost first (skips the tab and sort).
        if (overstockOnly) {
            // Composes with Box size missing (fix round 1): both checked = overstock rows with no box.
            return result.filter(s => s.overstock === true && (!boxMissingOnly || s.boxMissing))
                .sort((a, b) => (overstockUnits(b) * (b.priceListCost || 0)) - (overstockUnits(a) * (a.priceListCost || 0)));
        }
        if (priceLossOnly) {
            // Owner 2026-11-03: price-guard rows from every bucket, largest held-back order first; composes with Box size missing.
            return result.filter(s => s.priceLoss === true && (!boxMissingOnly || s.boxMissing))
                .sort((a, b) => (heldBackUnits(b) * (b.priceListCost || 0)) - (heldBackUnits(a) * (a.priceListCost || 0)));
        }

        // Task 8: with advice, the table shows one bucket at a time, in the bucket's own order
        // (soonest order-by, then most dollars at risk) until the user clicks a column header.
        if (advice) {
            result = result.filter(s => viewBucket(s) === bucketTab);
            if (!boxMissingOnly && !userSorted) return sortForBucket(result);
        }

        // Spec 2026-11-01 F3: rows with no shipping box, best sellers first (skips the user/bucket sort).
        if (boxMissingOnly) {
            return result.filter(s => s.boxMissing).sort((a, b) => (b.unitsSold30d || 0) - (a.unitsSold30d || 0));
        }

        result.sort((a, b) => compare(
            sortValue(a, sortField, enrichment, selectedItems),
            sortValue(b, sortField, enrichment, selectedItems), sortDir));

        return result;
    }, [preBucket, sortField, sortDir, enrichment, selectedItems, advice, bucketTab, userSorted, boxMissingOnly, overstockOnly, priceLossOnly]);

    // Spec 2026-11-03 §6: what the overstock rows tie up — units (available + inbound) and dollars at cost.
    const overstockSummary = useMemo(() => {
        if (!overstockOnly) return null;
        let units = 0, cost = 0, noCost = 0;
        filteredSuggestions.forEach(s => {
            const u = overstockUnits(s);
            units += u;
            if (s.priceListCost != null) cost += u * s.priceListCost; else noCost += 1;
        });
        return { products: filteredSuggestions.length, units, cost, noCost };
    }, [overstockOnly, filteredSuggestions]);
    // Owner 2026-11-03: what the price guard is holding back — units and dollars at cost not ordered until the price is fixed.
    const priceLossSummary = useMemo(() => {
        if (!priceLossOnly || overstockOnly) return null;   // the Overstock filter wins when both are checked
        let units = 0, cost = 0;
        filteredSuggestions.forEach(s => { const u = heldBackUnits(s); units += u; cost += u * (s.priceListCost || 0); });
        return { products: filteredSuggestions.length, units, cost };
    }, [priceLossOnly, filteredSuggestions]);

    // Task 8 review I2: the meter and Add all work on the Order-now rows the purchaser can see.
    // Skipped Order-now rows count toward the total (they are handled) and are named separately.
    const visibleOrderNow = useMemo(() => preBucket.filter(s => s.bucket === 'ORDER_NOW'), [preBucket]);
    const meter = useMemo(() => {
        const { total, unhandled } = orderNowHandled(visibleOrderNow, selectedItems);
        const skipped = visibleOrderNow.filter(s => s.skip).length;
        // Pre-rank (supplierFiltered): Order-now rows the rank cutoff hides are hidden by a filter too.
        const hiddenByFilters = Math.max(0,
            supplierFiltered.filter(s => s.bucket === 'ORDER_NOW').length - visibleOrderNow.length);
        return { total, unhandled, skipped, hiddenByFilters };
    }, [visibleOrderNow, selectedItems, supplierFiltered]);

    // Task 8 review I1: the BB Holder filter lives in a header hidden behind Show details, so
    // when it is active and hidden it announces how many rows of this tab it removes.
    const bbHiddenInTab = useMemo(() => {
        if (!advice || bbFilter.length === 0 || showDetails) return null;
        const inTab = (rows) => rows.filter(s => viewBucket(s) === bucketTab).length;
        return inTab(applyRestFilters(searchStatusNoBb)) - inTab(preBucket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advice, bbFilter, showDetails, bucketTab, searchStatusNoBb, preBucket,
        marginMinPct, showOnlyNeedRestock, excludeCheated, cheatDayThreshold]);

    // Task 6: one consolidated status card + one coverage card, both computed over rankFiltered
    // (the below-cutoff, everyday view — same set "Products under rank cutoff" counts). Unknown
    // never counts as a bad number and never masquerades as a good one: every percentage states
    // its own denominator (rows with a KNOWN value) and how many rows were excluded as unknown.
    const statusCounts = useMemo(() => {
        const counts = { OUT: 0, LOW: 0, OK: 0, unknown: 0 };
        rankFiltered.forEach(s => {
            if (s.stockStatus === 'OUT') counts.OUT++;
            else if (s.stockStatus === 'LOW') counts.LOW++;
            else if (s.stockStatus === 'OK') counts.OK++;
            else counts.unknown++;
        });
        return counts;
    }, [rankFiltered]);

    const coverageSummary = useMemo(() => {
        const knownStatus = rankFiltered.filter(s => s.stockStatus != null);
        const outCount = knownStatus.filter(s => s.stockStatus === 'OUT').length;
        const outPct = knownStatus.length > 0 ? Math.round((outCount / knownStatus.length) * 100) : null;
        const outExcluded = rankFiltered.length - knownStatus.length;

        // Unweighted reach (cov1dReachPct) — the same lens the Coverage column sorts by. Averaged
        // over rows the nightly scorecard actually measured; most rows will be excluded (the
        // scorecard's universe is the regional crawler's pinned set, 227 of 3,002 catalog ASINs).
        const knownReach = rankFiltered.filter(s => s.cov1dReachPct != null);
        const avgReach = knownReach.length > 0
            ? Math.round(knownReach.reduce((sum, s) => sum + s.cov1dReachPct, 0) / knownReach.length)
            : null;
        const reachExcluded = rankFiltered.length - knownReach.length;

        return { outPct, outKnown: knownStatus.length, outExcluded, avgReach, reachKnown: knownReach.length, reachExcluded };
    }, [rankFiltered]);

    // Task 6 required fix: the old sum used `s.priceListCost || 0`, so an unknown cost silently
    // contributed $0 to a dollar total used for buying decisions. Unknown-cost rows are now
    // excluded from the sum entirely, and the exclusion count is stated beside it.
    // Important 5 fix: this used to sum over `suggestions` (the whole 3,002-ASIN catalog) while
    // its three sibling cards (statusCounts, coverageSummary, "Products in view") all read
    // `rankFiltered` — one card describing the whole catalog beside three describing the filtered
    // view. Now consistent with its neighbours.
    const restockValueSummary = useMemo(() => {
        const needRestock = rankFiltered.filter(s => s.suggestedOrderQty > 0);
        const knownCost = needRestock.filter(s => s.priceListCost != null);
        const total = knownCost.reduce((sum, s) => sum + s.suggestedOrderQty * s.priceListCost, 0);
        return { total, excluded: needRestock.length - knownCost.length };
    }, [rankFiltered]);

    // Important 4: neither OUT/LOW/OK nor the old cards counted the set "Add All Needing Restock"
    // and the Restock Value card actually operate on (suggestedOrderQty > 0) — OUT alone conflates
    // "we sell this and ran out" with "we have never carried this". This gives the button and the
    // headline a matching number, without attempting to split OUT by sales history (an owner
    // decision, not this fix).
    //
    // Fix wave (Important 1): the comment above states this exists to give the button a matching
    // number, so counting over `rankFiltered` while `selectAllNeedRestock` iterates
    // `filteredSuggestions` was a violated invariant, not a design choice — latent while the page
    // had few filters, made the default case as soon as Task 3 added the status/margin controls.
    // Now sourced from `filteredSuggestions` so the two can never diverge again.
    const needRestockCount = useMemo(
        () => filteredSuggestions.filter(s => s.suggestedOrderQty > 0).length,
        [filteredSuggestions]
    );

    const draftItems = Object.values(selectedItems).filter(i => i.quantity > 0);
    const draftTotalUnits = draftItems.reduce((s, i) => s + i.quantity, 0);
    const draftTotalSfp = draftItems.reduce((s, i) => s + (i.sfpQuantity || 0), 0);
    // Important 6 fix: the old sum used `i.unitCost ? ... : 0` — an unknown cost silently
    // contributed $0 to a dollar total used for buying decisions (and a genuine $0.00 cost was
    // also wrongly treated as unknown by the truthy check). Unknown-cost lines are now excluded
    // entirely, and the exclusion count is stated beside the total — matching the Restock Value
    // card's wording. Note: this is a display-only fix. createPurchaseOrder's persistence is
    // untouched — POCreationService.java still writes Amount = 0 for a null unit cost, so a
    // fabricated zero can still reach calibrate_supplier_pos on create; flagged, not fixed here.
    const draftKnownCost = draftItems.filter(i => i.unitCost != null);
    const draftTotalCost = draftKnownCost.reduce((s, i) => s + i.unitCost * i.quantity, 0);
    const draftCostExcluded = draftItems.length - draftKnownCost.length;

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <span className="text-gray-400 ml-1">&#8693;</span>;
        return <span className="text-indigo-400 ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    // Global constraint: "A hidden filter must announce itself." A full (4) or empty (0) selection
    // both mean "no constraint" (see the searchStatusFiltered memo above) and must show no marker —
    // only a genuinely partial selection narrows the table.
    const statusFilterActive = statusFilter.length > 0 && statusFilter.length < 4;

    // Task 6: the Status column's cell renderer. Depth only — OUT/LOW/OK from stockStatus, dash
    // when unknown (missing stock or velocity data). Never guesses a status from suggestedOrderQty.
    const stockStatusBadge = (status) => {
        if (status === 'OUT') return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">Out of Stock</span>;
        if (status === 'LOW') return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Low</span>;
        if (status === 'OK') return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">OK</span>;
        return <span className="text-gray-400 text-sm">—</span>;
    };

    // BB Holder (owner brief 2026-09-13, CORRECTED same day): who holds the Buy Box RIGHT NOW,
    // from the regional crawler's latest day (regional_buybox_data, most ZIPs) — a different question from BB
    // Win's 28-day historical rate.
    //
    // Correction to the original brief: a null bb_seller_id on a fresh check does NOT mean "nobody
    // holds the Buy Box". Traced to source: the snapshot is derived from schluter_offer_json's
    // $.summary.buyBoxPrices[0].sellerId — a Buy Box PRICE exists on every row, but Amazon
    // discloses the seller id on only ~77% of them, and the rows missing it never carry an
    // IsBuyBoxWinner offer either, so there is no way to recover the holder independently.
    // Asserting "nobody holds it" here would state a fact this data does not support — exactly the
    // doctrine violation ("unknown is never zero") this column exists to avoid. There is therefore
    // no third "no holder" state, only two known states plus unknown:
    //   'US'         — an unmissable green badge: this row needs no competitive attention, the
    //                  whole point of the column.
    //   'COMPETITOR' — the resolved seller name, or the raw seller id when it does not resolve
    //                  (bbHolderName already carries whichever one applies).
    //   null         — unknown, a dash. Three different reasons collapse into the same dash here
    //                  (undisclosed-but-fresh, stale, never checked) but stay distinguishable in
    //                  the tooltip — see bbHolderTitle below.
    const bbHolderCellContent = (item) => {
        if (item.bbHolderState === 'US') {
            return <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-800">You hold it</span>;
        }
        if (item.bbHolderState === 'COMPETITOR') {
            return <span className="text-sm text-orange-700 font-medium">{item.bbHolderName}</span>;
        }
        return <span className="text-gray-400 text-sm">—</span>;
    };

    // Tooltip convention matches the MP Inventory cell (owner, verbatim: "the tool tip should
    // indicate the date it was checked"): when a value is shown, say when it was checked; when
    // dashed, say WHY. The three unknown reasons genuinely differ, so the dash still distinguishes
    // them even though the cell text is the same for all three:
    //   bbHolderDashReason === 'UNDISCLOSED' — checked recently, Amazon did not disclose a seller
    //   bbHolderDashReason === 'STALE'       — last checked more than 48h ago
    //   neither (no reason, no date)          — never checked at all
    const bbHolderTitle = (item) => {
        if (item.bbHolderState) return `Buy Box checked ${item.bbHolderCheckedAt}`;
        if (item.bbHolderDashReason === 'UNDISCLOSED') {
            return `checked ${item.bbHolderCheckedAt} — Amazon did not disclose who holds the Buy Box`;
        }
        if (item.bbHolderDashReason === 'STALE') {
            return `unknown — last checked ${item.bbHolderCheckedAt}, more than 48 hours ago`;
        }
        return 'unknown — never checked';
    };

    // Task 6: shared formatter for the three coverage lenses — a null lens dashes rather than
    // reading as 0%, which would look like "terrible coverage" for an ASIN never measured.
    const fmtPct = (v) => v == null ? '—' : `${Math.round(v)}%`;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Purchase Order Creation</h1>
                    <p className="mt-1 text-sm text-gray-500">Suggest restock quantities based on sales velocity and current inventory</p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Coverage Target %:</label>
                        <input
                            type="number"
                            min="10"
                            max="100"
                            data-testid="coverage-target"
                            value={coverageTargetPct}
                            onChange={e => handleCoverageTargetChange(parseInt(e.target.value) || 80)}
                            className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        {/* Measured cover curve: 9 FCs->80%, flattening hard past 12 (14->95%,
                            17->100%) — FCs 13-17 each add roughly one metro. A target pushed near
                            100 roughly doubles the FC count for a few points of reach, so this is
                            a caution, not a hard cap. Explicit Number() comparison — `null > 95`
                            is false but `null < 95` is true, so a coerced comparison would gate
                            the wrong direction if this state were ever nullable. */}
                        {Number(coverageTargetPct) > 95 && (
                            <span data-testid="coverage-target-caution" className="text-xs text-gray-400 whitespace-nowrap">
                                Above 95% the cover curve has flattened — FCs 13-17 each add roughly one metro
                            </span>
                        )}
                    </div>
                    <button type="button" data-testid="reorder-settings-button" onClick={() => setReorderSettingsOpen(true)}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600">
                        Lead time &amp; cover
                    </button>
                    {reorderSettings && (
                        <span data-testid="reorder-settings-summary" className="text-xs text-gray-500 whitespace-nowrap">
                            {reorderSettings.coverDays}d cover at landing · {reorderSettings.supplierLeadDays + reorderSettings.handlingDays + reorderSettings.amazonInboundDays}d lead
                        </span>
                    )}
                    <button
                        type="button"
                        aria-label="Spread settings"
                        title="Spread settings"
                        onClick={() => setSpreadSettingsOpen(true)}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600"
                    >
                        &#9881;
                    </button>
                    <button
                        onClick={() => fetchSuggestions()}
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={handleRefreshCoverage}
                        disabled={refreshingCoverage}
                        title="Recomputes the whole scorecard — the same nightly recompute ScorecardJobs already runs at 06:00 ET, run on demand. Covers only the regional crawler's pinned set (227 of 3,002 catalog ASINs); it will not produce coverage for an ASIN with no current stock or outside that set."
                        className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {refreshingCoverage ? 'Refreshing…' : 'Refresh coverage'}
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            {!loading && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {/* Fix wave (Important 1): this card counts rankFiltered — before search, status,
                        margin, restock and cheat filters apply — so "in view" was false whenever
                        any transient filter was active. "Under rank cutoff" states what the number
                        actually is. */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-500">Products under rank cutoff</p>
                        <p className="text-2xl font-bold text-gray-900">{rankFiltered.length}</p>
                    </div>
                    {/* Task 6: one consolidated Status card (OUT/LOW/OK), replacing the three
                        separate Need-Restock / Low-Stock / Out-of-Stock cards. */}
                    {D(<div className="bg-white rounded-lg shadow p-4" data-testid="status-card">
                        <p className="text-sm text-gray-500">Status</p>
                        <div className="mt-1 flex gap-4">
                            <div>
                                <p className="text-xs text-gray-500">OUT</p>
                                <p className="text-xl font-bold text-red-600">{statusCounts.OUT}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">LOW</p>
                                <p className="text-xl font-bold text-orange-600">{statusCounts.LOW}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">OK</p>
                                <p className="text-xl font-bold text-green-600">{statusCounts.OK}</p>
                            </div>
                        </div>
                        {/* Important 4: the button below ("Add All Needing Restock") and the Restock
                            Value card both operate on suggestedOrderQty > 0 — a set no card counted. */}
                        <p className="text-xs text-gray-600 mt-1">{needRestockCount} need restock</p>
                        {/* Minor 11: OUT+LOW+OK+unknown partitions the set exactly — nothing is
                            "excluded" here, so this states the count is unknown, not omitted. */}
                        {statusCounts.unknown > 0 && (
                            <p className="text-xs text-gray-400 mt-1">{statusCounts.unknown} unknown status</p>
                        )}
                    </div>)}
                    {/* Task 6: out-of-stock % and average <=1-day reach %, each stating its own
                        denominator (known rows) and exclusion count (unknown rows) — a small
                        denominator must never masquerade as a good number. */}
                    {D(<div className="bg-white rounded-lg shadow p-4" data-testid="coverage-card">
                        <p className="text-sm text-gray-500">Coverage</p>
                        <div className="mt-1 flex gap-4">
                            <div>
                                <p className="text-xs text-gray-500">Out of stock</p>
                                <p className="text-xl font-bold text-red-600">{fmtPct(coverageSummary.outPct)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Avg ≤1d reach</p>
                                <p className="text-xl font-bold text-indigo-600">{fmtPct(coverageSummary.avgReach)}</p>
                            </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                            {coverageSummary.outKnown} of {rankFiltered.length} have a known status ({coverageSummary.outExcluded} excluded, unknown);{' '}
                            {coverageSummary.reachKnown} of {rankFiltered.length} are in the scorecard ({coverageSummary.reachExcluded} excluded, unscored)
                        </p>
                    </div>)}
                    {D(<div className="bg-white rounded-lg shadow p-4" data-testid="restock-value-card">
                        <p className="text-sm text-gray-500">Restock Value</p>
                        <p className="text-2xl font-bold text-indigo-600">
                            ${restockValueSummary.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </p>
                        {restockValueSummary.excluded > 0 && (
                            <p className="text-xs text-gray-400 mt-1">{restockValueSummary.excluded} excluded — unknown cost</p>
                        )}
                    </div>)}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex flex-wrap items-center gap-4">
                    <input
                        type="text"
                        placeholder="Search by name, ASIN, or SKU..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="flex-1 min-w-[200px] rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                    />
                    {/* Owner 2026-09-11: the Supplier picker, matching the supplier column. Options
                        are derived from the loaded catalog, so a supplier that disappears stops being
                        offered and one that appears is offered without a code change. */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap" htmlFor="supplier-filter-select">
                            Supplier
                        </label>
                        <select
                            id="supplier-filter-select"
                            data-testid="supplier-filter"
                            value={effectiveSupplier}
                            onChange={e => setSupplierFilter(e.target.value)}
                            title="Which supplier's products can be reordered here. Opens on Schluter; pick another, or All Suppliers, to widen it. Products whose price-list row carries no supplier label appear under 'no supplier set'."
                            className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        >
                            <option value={ALL_SUPPLIERS}>All Suppliers</option>
                            {supplierOptions.map(v => (
                                <option key={v} value={v}>
                                    {v === NO_SUPPLIER ? '(no supplier set)' : v}
                                </option>
                            ))}
                        </select>
                        {effectiveSupplier !== supplierFilter && (
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                                no “{supplierFilter}” rows — showing all
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap">Rank cutoff</label>
                        <input
                            type="text"
                            value={rankCutoff}
                            onChange={e => handleRankCutoffChange(e.target.value)}
                            title="Filters which products are shown here, AND gates which ASINs the server may recommend a coverage-driven quantity for — a product ranked over this cutoff gets depth advice only. Blank shows every ASIN in the view, but the server still gates coverage seeding at its own 500,000 default even when no cutoff is sent."
                            className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        {rankCutoff.trim() === '' && (
                            <span className="text-xs text-gray-500 whitespace-nowrap">all — includes unranked (coverage seeding still capped at the server's 500,000 default)</span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap" htmlFor="margin-threshold-input">
                            Margin &gt;
                        </label>
                        <input
                            id="margin-threshold-input"
                            type="number"
                            data-testid="margin-threshold"
                            value={marginMinPct}
                            onChange={e => setMarginMinPct(e.target.value)}
                            title="Compares against whichever basis each row's margin used: the listing price where the Fee Preview report covers the ASIN, MAP otherwise. MAP is the floor, so MAP-basis rows carry systematically lower percentages and will be filtered out first — a missing product here is not a bug."
                            className="w-16 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        />
                        <span className="text-sm text-gray-700">%</span>
                        {/* Critical fix (C2): excluding unknown-percent rows is correct, but saying
                            nothing about the magnitude is not — only 216 of 3,002 catalog ASINs can
                            have a non-null grossMarginPct at all, so a "Margin > 10" filter can drop
                            2,786 rows for having no measurable margin, indistinguishable from a real
                            low-margin exclusion unless this count is shown. Same wording/styling
                            idiom as the Restock Value and draft-total exclusion notes. */}
                        {marginExcludedCount > 0 && (
                            <span className="text-xs text-gray-400 whitespace-nowrap">{marginExcludedCount} excluded — unknown margin</span>
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            data-testid="only-need-restock"
                            checked={showOnlyNeedRestock}
                            onChange={e => setShowOnlyNeedRestock(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Only show items needing restock
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            data-testid="box-missing-filter"
                            checked={boxMissingOnly}
                            onChange={e => setBoxMissingOnly(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Box size missing
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700"
                        title="Products the overstock guard held at 0: more days of cover than the overstock setting while the Buy Box share is low. Shown across all tabs.">
                        <input
                            type="checkbox"
                            data-testid="overstock-filter"
                            checked={overstockOnly}
                            onChange={e => setOverstockOnly(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Overstock — liquidate or recall
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700"
                        title="Products not ordered because a seller below MAP (or simply cheaper) wins the Buy Box: zero until the price is fixed. Shown across all tabs.">
                        <input
                            type="checkbox"
                            data-testid="price-loss-filter"
                            checked={priceLossOnly}
                            onChange={e => setPriceLossOnly(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Cheated — not ordered
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={excludeCheated}
                            onChange={e => setExcludeCheated(e.target.checked)}
                            aria-label="Exclude products cheated at or above the threshold"
                        />
                        Exclude products cheated ≥
                        <input
                            type="number"
                            min="1"
                            max="100"
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                            value={cheatThresholdPct}
                            onChange={e => setCheatThresholdPct(e.target.value)}
                            aria-label="cheat threshold percent"
                        />
                        % of the last 30 days
                        {cheatDayThreshold != null && (
                            <span className="text-gray-400">({cheatDayThreshold}+ days)</span>
                        )}
                    </label>
                    <button
                        onClick={selectAllNeedRestock}
                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                    >
                        Add All Needing Restock
                    </button>
                </div>
                {/* Minor fix (3): the enrichment fetch caps at the first 500 rankFiltered rows in
                    server order (see the fetch effect above) — a limit that was effectively
                    unreachable before the rank cutoff could be blanked and other filters narrowed
                    the table to rows sitting past that index. When it's in play, MP vel·7d, Local
                    SFP, 1d/2d and To 100% ≤1d all dash with no visible reason unless stated here. */}
                {rankFiltered.length > 500 && (
                    <p className="text-xs text-gray-400 mt-2">
                        {rankFiltered.length.toLocaleString()} products under the rank cutoff — enrichment (MP vel·7d, Local SFP, 1d/2d, To 100% ≤1d) covers only the first 500 in server order; the rest dash.
                    </p>
                )}
            </div>

            {spreadSummary && (
                <div data-testid="spread-summary" className="text-xs text-gray-600 dark:text-gray-300">
                    <div className="mb-1">
                        At target: {spreadSummary.atTarget ?? 0} of {spreadSummary.products} FBA products
                        {spreadSummary.salesShareBelow5Pct != null && <> · {spreadSummary.salesShareBelow5Pct}% of 30-day units from products in fewer than 5 FCs</>}
                        {' '}({spreadSummary.snapshotDate})
                    </div>
                    {Array.isArray(spreadSummary.newSpeedLoss) && spreadSummary.newSpeedLoss.length > 0 && (
                        <div data-testid="new-speed-loss">
                            Newly losing on speed:{' '}
                            {spreadSummary.newSpeedLoss.map((a, i) => (
                                <span key={a.asin}>
                                    {i > 0 && ', '}
                                    <a className="text-indigo-600 hover:underline" href={`/shipment-planner-v2?refill=${encodeURIComponent(a.asin)}`}>{a.asin}</a>
                                    {Array.isArray(a.regions) && a.regions.length ? ` (${a.regions.join(', ')})` : ''}
                                </span>
                            ))}
                        </div>
                    )}
                    {Array.isArray(spreadSummary.newAlerts) && spreadSummary.newAlerts.length > 0 && (
                        <div>
                            New region alerts:{' '}
                            {spreadSummary.newAlerts.map((a, i) => (
                                <span key={a.asin}>
                                    {i > 0 && ', '}
                                    <a className="text-indigo-600 hover:underline" href={`/shipment-planner-v2?refill=${encodeURIComponent(a.asin)}`}>{a.asin}</a>
                                    {a.alertRegions ? ` (${a.alertRegions})` : ''}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Spec 2026-11-01 F5: a failed advisor must not pass silently as an empty reorder view. */}
            {suggestions.some(s => s.adviceStatus === 'FAILED') && (
                <div data-testid="advice-failed-banner" className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200">
                    Reorder advisor unavailable — quantities are the basic estimate (no reasons, buckets, spread or lead time). Refresh to retry.
                </div>
            )}

            {/* Task 8: action-bucket tabs (spec §9), only when the payload carries advice. */}
            {advice && !loading && (
                <div className="bg-white rounded-lg shadow p-3 flex flex-wrap items-center gap-2">
                    {BUCKETS.map(b => {
                        const sum = bucketCounts[b.key];
                        const active = bucketTab === b.key;
                        return (
                            <button key={b.key} type="button" data-testid={`bucket-tab-${b.key}`}
                                    aria-pressed={active}
                                    onClick={() => { setBucketTab(b.key); setUserSorted(false); }}
                                    className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap border ${active
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                                {b.label} · {sum.count} · ${Math.round(sum.dollars).toLocaleString()}
                            </button>
                        );
                    })}
                    {bbHiddenInTab != null && (
                        <span data-testid="hidden-filter-chip"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-amber-50 text-amber-800 border border-amber-200">
                            BB Holder filter active — {bbHiddenInTab} rows hidden ·{' '}
                            <button type="button" onClick={() => setBbFilter([])}
                                    className="underline hover:text-amber-900">Clear</button>
                        </span>
                    )}
                    <button type="button" data-testid="details-toggle"
                            aria-pressed={showDetails}
                            onClick={() => setShowDetails(v => !v)}
                            className="ml-auto px-3 py-1.5 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50">
                        {showDetails ? 'Hide details' : 'Show details'}
                    </button>
                    {bucketTab === 'ORDER_NOW' && (
                        <div className="w-full flex flex-wrap items-center gap-3 pt-1">
                            <span data-testid="handled-meter" className="text-sm text-gray-700"
                                  title="Handled = in the current PO draft or skipped.">
                                {`${meter.unhandled} of ${meter.total} not yet handled`}
                                {meter.skipped > 0 && ` · ${meter.skipped} skipped`}
                                {meter.hiddenByFilters > 0 && ` · ${meter.hiddenByFilters} more hidden by filters`}
                            </span>
                            <button type="button" data-testid="add-all-order-now"
                                    onClick={addAllOrderNow}
                                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                                Add all in Order now to draft
                            </button>
                        </div>
                    )}
                </div>
            )}

            {overstockSummary && (
                <div data-testid="overstock-summary" className="mb-2 text-sm text-gray-700 dark:text-gray-300"
                    title="Units = FBA available + inbound; cost = units × price-list cost. Across all tabs.">
                    {overstockSummary.products} products · {overstockSummary.units.toLocaleString('en-US')} units ·
                    {' '}${Math.round(overstockSummary.cost).toLocaleString('en-US')} at cost
                    {overstockSummary.noCost > 0 && <span className="text-gray-400"> · {overstockSummary.noCost} without a cost</span>}
                </div>
            )}
            {priceLossSummary && (
                <div data-testid="price-loss-summary" className="mb-2 text-sm text-gray-700 dark:text-gray-300"
                     title="Held back by the price guard: units and dollars at cost that would have been ordered.">
                    {priceLossSummary.products} products · {priceLossSummary.units.toLocaleString('en-US')} units held back ·
                    {' '}${Math.round(priceLossSummary.cost).toLocaleString('en-US')} at cost
                </div>
            )}

            {/* Main Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <span className="ml-3 text-gray-500">Loading suggestions...</span>
                    </div>
                ) : (
                    // Owner, verbatim: "when scrolling down the table header should be sticky and
                    // remain visible". CSS trap (see the task brief): this div was already a
                    // scroll container on BOTH axes — `overflow-x-auto` alone forces the computed
                    // overflow-y to `auto` too (never `visible`) per the CSS spec, so `sticky top-0`
                    // on the header stuck to THIS div; but with no height cap the div never scrolls,
                    // so nothing ever stuck. The fix is the height cap below (max-h + overflow-y-auto)
                    // so the table body scrolls inside this div and the sticky header (on the <th>
                    // cells themselves, not <thead> — unreliable on table sections cross-browser)
                    // has something to pin against. overflow-x-auto stays: ~17 columns need it.
                    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-26rem)]">
                        <table className="min-w-full divide-y divide-gray-200">
                            {/* Owner, 2026-09-08: "each header should have a tooltip that explains
                                what it is" — every th carries a title in plain language, stating
                                what the number measures and where it comes from. */}
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 sticky top-0 z-10 bg-gray-50" title="Row number in the current view — renumbers as you filter or sort.">#</th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 sticky top-0 z-10 bg-gray-50" title="Select every row needing restock, or clear the whole draft.">
                                        <input
                                            type="checkbox"
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            onChange={e => {
                                                if (e.target.checked) selectAllNeedRestock();
                                                else clearDraft();
                                            }}
                                            checked={draftItems.length > 0 && draftItems.length === filteredSuggestions.filter(s => s.suggestedOrderQty > 0).length}
                                        />
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('productName')}
                                        title="Product name, ASIN and SKU. Hover a name to see where its FBA stock sits on the map.">
                                        Product <SortIcon field="productName" />
                                    </th>
                                    {advice && (
                                        <th data-testid="why-header"
                                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                            title="Why this product is in its bucket — the server's reasons, coloured by kind. The bold chip drove the bucket; click a chip for its sentence.">
                                            Why
                                        </th>
                                    )}
                                    {advice && (
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50"
                                            onClick={() => handleSort('orderBy')}
                                            title="Last day to place the order so it lands before stock runs out, given the lead time above. Red = today or already past.">
                                            Order by <SortIcon field="orderBy" />
                                        </th>
                                    )}
                                    {/* Owner, verbatim: "the status coulmn selectors should actually be shown
                                        when the 'status' column header is clicked. not as seperate check
                                        boxes" — spreadsheet-autofilter pattern (owner decision: the menu
                                        carries BOTH sort and filter, so sorting by status is never lost).
                                        Closed by default; the trigger is a real <button> so it's keyboard-
                                        reachable and carries its own accessible name without touching the
                                        th's implicit columnheader role. `sticky` (Part 2) already makes this
                                        th a positioning context for the absolutely-positioned menu below —
                                        no separate `relative` needed. */}
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                        title="Computed stock depth: OUT (no FBA stock), LOW (under 30 days of supply), OK (30+ days). Depth only — see the Coverage column for reach, a separate concern with a separate remedy. Dash = unknown (missing stock or velocity data)."
                                        ref={statusMenuBoxRef}>
                                        <button
                                            type="button"
                                            data-testid="status-header-trigger"
                                            aria-haspopup="true"
                                            aria-expanded={statusMenuOpen}
                                            aria-label="Status: sort and filter menu"
                                            onClick={() => setStatusMenuOpen(o => !o)}
                                            className="flex items-center gap-1 w-full text-left cursor-pointer hover:text-gray-700 font-medium uppercase tracking-wider"
                                        >
                                            Status <SortIcon field="stockStatus" />
                                            {statusFilterActive && (
                                                <span data-testid="status-filter-active"
                                                      title="Status filter is narrowing the table — not every row is shown."
                                                      className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-indigo-500" />
                                            )}
                                        </button>
                                        {statusMenuOpen && (
                                            <div className="absolute left-0 top-full z-30 mt-1 w-52 rounded-md border border-gray-200 bg-white shadow-lg p-2 normal-case font-normal text-gray-700">
                                                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-1 pb-1">Sort</div>
                                                <button type="button" data-testid="status-sort-out-first"
                                                        onClick={() => sortStockStatus('desc')}
                                                        className="w-full text-left px-2 py-1 text-xs text-gray-700 hover:bg-indigo-50 rounded">
                                                    ↑ Out first
                                                </button>
                                                <button type="button" data-testid="status-sort-ok-first"
                                                        onClick={() => sortStockStatus('asc')}
                                                        className="w-full text-left px-2 py-1 text-xs text-gray-700 hover:bg-indigo-50 rounded">
                                                    ↓ OK first
                                                </button>
                                                <hr className="my-2 border-gray-100" />
                                                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-1 pb-1">Show</div>
                                                {STATUS_OPTIONS.map(({ value, testId, label, title: optionTitle }) => (
                                                    <label key={value} title={optionTitle}
                                                           className="flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-indigo-50 rounded cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            data-testid={testId}
                                                            checked={statusFilter.includes(value)}
                                                            onChange={() => setStatusFilter(prev => prev.includes(value)
                                                                ? prev.filter(v => v !== value)
                                                                : [...prev, value])}
                                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        {label}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </th>
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('unitsSold30d')}
                                        title="Units sold in the last 30 days, from Amazon order history — FBA orders only (cancelled orders excluded).">
                                        Sold/30d <SortIcon field="unitsSold30d" />
                                    </th>)}
                                    {/* Owner 2026-09-11: Rank Vel under Daily Vel. The pairing is the
                                        point — ours vs what the whole listing sells is the capture
                                        gap, and the caveat below is the common case: 111 of 223
                                        scored rows sit below the model's truth-set floor. */}
                                    {D(<th data-testid="vel-header"
                                        className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50"
                                        onClick={() => handleSort('dailyVelocity')}
                                        title="Daily Vel = OUR units sold per day (Sold/30d ÷ 30). Rank Vel = units per day implied by the sales rank for the LISTING AS A WHOLE — every seller on it, not us — from the scorecard's rank curve. The gap between them is demand we are not capturing. Treat Rank Vel as an UPPER BOUND: the curve was fitted over ranks ~357k-2.5M and is extrapolated, and optimistic, below that (roughly 2x at the head). Diagnostic only — it drives no order quantity.">
                                        <div className="leading-tight">
                                            <div>Daily Vel. <SortIcon field="dailyVelocity" /></div>
                                            <div className="text-[10px] normal-case text-gray-400">Rank Vel</div>
                                        </div>
                                    </th>)}
                                    {/* Owner, 2026-09-11: "consolidate the FBA Avail, FBA Unsell, FBA Used,
                                        Local SFP to the same column" — same grouped-column precedent as
                                        Unit Economics and Marketplace Pros. All four labels stay
                                        independently clickable sort targets; all four original tooltips'
                                        content is preserved below, merged into the one header cell that
                                        now covers all four. Local SFP moves here from its old position
                                        (it used to sit after DOS) — its td there is removed below.
                                        Local SFP is different from the other three: SORT_ACCESSORS.
                                        localSfpUnits reads the enrichment envelope, which only ever
                                        covers the first 500 rows in server order — so a dash on that
                                        line can mean "not yet enriched", not "no units", a different
                                        fact from the other three lines' dashes (absent from today's AFN
                                        inventory batch). The tooltip states this split explicitly, same
                                        as the MP column does for MP Inv vs MP vel·7d. */}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                        title="FBA Avail: sellable NEW-condition units currently at Amazon, from GET_AFN_INVENTORY_DATA — New only; used-condition sellable stock is the separate FBA Used line below. Covers most of the catalog. Dash means the ASIN was absent from today's inventory batch (unknown), never zero stock. FBA Unsell.: unsellable units currently at Amazon (damaged, defective, etc.). Excluded from FBA Avail. FBA Used: used-condition units in FBA (sellable). FBA only — local used stock is not yet counted here. Local SFP: units in local SFP pallet sections (Orlando warehouse). This line reads the enrichment envelope, which covers only the first 500 rows in server order — its dash can mean not yet enriched, not necessarily zero units, unlike the dash on the three lines above.">
                                        <div className="flex flex-col items-end gap-0.5">
                                            {[['fbaAvailable', 'FBA Avail'], ['fbaUnsellable', 'FBA Unsell.'],
                                              ['fbaUsedSellable', 'FBA Used'], ['localSfpUnits', 'Local SFP']].map(([field, label]) => (
                                                <span key={field}
                                                      data-testid={`stock-sort-${field}`}
                                                      onClick={() => handleSort(field)}
                                                      className="cursor-pointer hover:text-gray-700 whitespace-nowrap">
                                                    {label} <SortIcon field={field} />
                                                </span>
                                            ))}
                                        </div>
                                    </th>)}
                                    {/* Owner, 2026-09-08: "und mp inv, indicate the velocity based on the past
                                        7 days of inventory movement for MPs". Task 2, 2026-09-10:
                                        consolidated into one column (Unit Economics precedent) — both
                                        labels stay independently clickable sort targets; both original
                                        tooltips' content is preserved below, just merged into the one
                                        header cell that now covers both.
                                        Fix wave (Critical 1): the old wording ("Dash = not tracked")
                                        was carried over from when a dash on the level line meant a
                                        real 0 — an operator sorting descending sees ~190 numbers then
                                        ~2,810 dashes and could read "not tracked" as "they carry
                                        none", a reason to buy. The truth is "nobody has looked
                                        recently" (never probed, or not probed in the last 48h). */}
                                    {/* Owner, 2026-09-11: "color code MP Inv column to a light red" — a
                                        column TINT (pale background), not red text: text colours are
                                        untouched below (text-purple-700 / text-gray-400 still carry the
                                        probed/unprobed distinction). Header gets a touch stronger
                                        (bg-red-100) so it still reads as a header against the tinted
                                        cell (bg-red-50). */}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-red-100 sticky top-0 z-10"
                                        title="Marketplace Pros' current inventory level for this ASIN, from our tracker's latest probe. The tracker covers 266 of 3,002 catalog ASINs. Dash means never probed, or not probed in the last 48 hours — NEVER that they carry none. 0 means we probed and the shelf was genuinely empty. MP vel·7d: their estimated sales/day from that tracked inventory movement over the last 7 days; restocks excluded. Dash = untracked, or under 7 days of history for velocity.">
                                        <div className="flex flex-col items-end gap-0.5">
                                            {[['mpInventory', 'MP Inv'], ['mpVel7d', 'MP vel·7d']].map(([field, label]) => (
                                                <span key={field}
                                                      data-testid={`mp-sort-${field}`}
                                                      onClick={() => handleSort(field)}
                                                      className="cursor-pointer hover:text-gray-700 whitespace-nowrap">
                                                    {label} <SortIcon field={field} />
                                                </span>
                                            ))}
                                        </div>
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('fbaInbound')}
                                        title="Units on their way to Amazon: working + shipped + receiving from Amazon's Manage FBA Inventory report (hourly; restock report as fallback). Dash = unknown, never none.">
                                        Inbound <SortIcon field="fbaInbound" />
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('onOrder')}
                                        title="Units on open supplier POs: ordered − received − canceled. Hover the number to see which POs.">
                                        On Order <SortIcon field="onOrder" />
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('hereForFba')}
                                        title="Units received from Schluter on open POs, not yet in an Amazon shipment and not kept for SFP. Counted as stock. Click a number to ship them in Planner V2. Dash = unknown.">
                                        Here for FBA <SortIcon field="hereForFba" />
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('daysOfSupply')}
                                        title="Days of supply: FBA Avail ÷ Daily Vel., computed from our own 30-day order history — not the restock report. Ignores inbound and on-order units. Dashes when daily velocity is zero (no meaningful figure) or FBA Avail is unknown.">
                                        DOS <SortIcon field="daysOfSupply" />
                                    </th>)}
                                    {/* Local SFP moved into the consolidated stock header above (Task A,
                                        2026-09-11) — see that <th>'s comment. */}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('gvs1Day')}
                                        title="% of demand seeing a ≤1-day / ≤2-day promise from currently stocked FCs.">
                                        1d/2d <SortIcon field="gvs1Day" />
                                    </th>)}
                                    {/* Task 6: all three <=1-day coverage lenses from the nightly scorecard, side by
                                        side — owner wants all three because which one represents reality is not yet
                                        known. Grouped-column convention: header carries the three labels, the cell
                                        carries bare values aligned by position. */}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('cov1dReachPct')}
                                        title="Three <=1-day coverage lenses from the nightly scorecard's latest snapshot: Reach = unweighted share of monitored metros reachable in <=1 day; Demand = demand-weighted (flatters — metros we're slow to also generate few orders, so they carry little weight); Effective = demand-weighted AND discounted for stock persistence. Most catalog ASINs are outside the regional crawler's pinned set (227 of 3,002) and simply dash — that's expected, not an error. Use Refresh coverage for an ad-hoc recompute.">
                                        <div className="leading-tight">
                                            <div>Reach <SortIcon field="cov1dReachPct" /></div>
                                            <div>Demand</div>
                                            <div>Effective</div>
                                        </div>
                                    </th>)}
                                    {/* FC spread target (spec 2026-10-28) replaces the old "To 100% ≤1d" column.
                                        Always shown (not a detail column): spread status must stay visible. */}
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('spread')}
                                        title="FC spread: how many Amazon FCs still lack this product against the per-region target, and the units (whole boxes) to place one box in each. Hover a value for the missing regions.">
                                        Spread <SortIcon field="spread" />
                                    </th>
                                    {/* Owner 2026-09-11. The tooltip states the weighting because the
                                        number is routinely misread: page-view weighted is NOT
                                        time-weighted, and the two diverge exactly when traffic is
                                        uneven across the window — which is when someone consults it. */}
                                    {D(<th data-testid="bb-win-header"
                                        className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50"
                                        onClick={() => handleSort('bbWinPct')}
                                        title="Share of the last 28 days' PAGE VIEWS on which we held the Buy Box (Amazon's own pv_bb_pct_28d, from the nightly scorecard's latest snapshot). Page-view weighted, not time weighted: 30% means 30% of views saw us in the box, not that we held it 30% of the time. Only currently-stocked ASINs are scored (223 of ~2,900) — every other row dashes, which means unmeasured, never 'we never win'.">
                                        <span data-testid="bb-win-sort">BB Win <SortIcon field="bbWinPct" /></span>
                                    </th>)}
                                    {/* Owner brief 2026-09-13 (corrected same day): who holds the Buy
                                        Box RIGHT NOW — placed beside BB Win (a 28-day historical
                                        rate) but answering a different question. Populated for only
                                        ~18% of the catalog by design (545 of 3,002 ASINs checked in
                                        the last 48h); a dash means unknown — stale, never checked, or
                                        (corrected) a fresh check where Amazon simply did not
                                        disclose the holder — never "no competitor". See
                                        bbHolderCellContent for the full trace. */}
                                    {D(<th data-testid="bb-holder-header"
                                        ref={bbMenuBoxRef}
                                        className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                        title="Who holds the Buy Box for regular shoppers: the seller winning it in the most of our 50 ZIP codes on the regional crawler's latest day (crawler covers ~340 ASINs). Dash means unknown: not crawled in the last 7 days, the latest crawl is older than 48h, or Amazon did not disclose the holder. A dash never means nobody holds the Buy Box.">
                                        <span className="inline-flex items-center gap-1">
                                            <span data-testid="bb-holder-sort" className="cursor-pointer hover:text-gray-700"
                                                  onClick={() => handleSort('bbHolder')}>BB Holder <SortIcon field="bbHolder" /></span>
                                            <button type="button" data-testid="bb-holder-filter-trigger"
                                                    aria-haspopup="true" aria-expanded={bbMenuOpen}
                                                    aria-label="Filter by Buy Box holder"
                                                    title="Show only products held by the sellers you pick"
                                                    onClick={() => setBbMenuOpen(o => !o)}
                                                    className="px-1 rounded hover:bg-gray-200 text-gray-500">▾</button>
                                            {bbFilter.length > 0 && (
                                                <span data-testid="bb-holder-filter-active"
                                                      title="BB Holder filter is narrowing the table — not every row is shown."
                                                      className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-500" />
                                            )}
                                        </span>
                                        {bbMenuOpen && (
                                            <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg p-2 normal-case font-normal text-gray-700 text-left">
                                                <div className="flex items-center justify-between px-1 pb-1">
                                                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Show holders</span>
                                                    <button type="button" data-testid="bb-holder-clear"
                                                            onClick={() => setBbFilter([])}
                                                            className="text-[11px] text-indigo-600 hover:underline">Clear</button>
                                                </div>
                                                <input type="search" data-testid="bb-holder-search"
                                                       value={bbMenuSearch} onChange={e => setBbMenuSearch(e.target.value)}
                                                       placeholder="Search sellers" aria-label="Search Buy Box holders"
                                                       className="w-full mb-1 px-2 py-1 border border-gray-300 rounded text-xs" />
                                                <div className="max-h-64 overflow-y-auto">
                                                    {bbHolderOptions
                                                        .filter(o => !bbMenuSearch || o.label.toLowerCase().includes(bbMenuSearch.toLowerCase())
                                                            || o.key.toLowerCase().includes(bbMenuSearch.toLowerCase()))
                                                        .map(o => (
                                                            <label key={o.key} title={o.key === 'US' || o.key === 'UNKNOWN' ? undefined : o.key}
                                                                   className="flex items-center gap-1 px-2 py-1 text-xs hover:bg-indigo-50 rounded cursor-pointer">
                                                                <input type="checkbox" data-testid={`bb-holder-option-${o.key}`}
                                                                       checked={bbFilter.includes(o.key)}
                                                                       onChange={() => setBbFilter(prev => prev.includes(o.key)
                                                                           ? prev.filter(k => k !== o.key) : [...prev, o.key])}
                                                                       className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                                                <span className="flex-1 truncate">{o.label}</span>
                                                                <span className="text-gray-400 tabular-nums">{o.count}</span>
                                                            </label>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('cheatedDays30')}
                                        title="Days with a MAP violation in the last 30 — diagnostic only; cheated-day demand is real demand.">
                                        Cheat·30d <SortIcon field="cheatedDays30" />
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('salesRank')}
                                        title="Current sales rank from live offer data — the same number the rank-cutoff filter reads.">
                                        Rank <SortIcon field="salesRank" />
                                    </th>)}
                                    {/* Box configuration column. unitsPerBox rides the suggestion row (server,
                                        2026-09-13) — known for only 53 of 2,900 ASINs today, which is exactly
                                        why coverage-driven quantities stay near-inert. Setting a pack HERE, at
                                        the buying-decision page, is the point: it writes the same
                                        calibrate_asin_packaging_defaults row the shipment planner edits (one
                                        source of truth), so coverage can size a quantity for a product before
                                        it is ever shipped. */}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                        title="Units per box (calibrate_asin_packaging_defaults — the same packaging default the shipment planner edits). Unknown for most of the catalog. Click a row's value or 'Set pack' to add or change it; saving refreshes the suggestions so the coverage-driven quantity reflects the new pack immediately.">
                                        Box
                                    </th>)}
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('suggestedOrderQty')}
                                        title={advice
                                            ? 'Days-of-supply shortfall (target days × daily sales, minus available, inbound and on order) PLUS units for missing warehouses when sales show placement is costing us — stock already in other regions does not reduce those, spare on-order units do. Rounded to case pack and supplier minimum. The bold reason is the larger part.'
                                            : 'The larger of the depth-of-supply need (target days × daily velocity) and the ≤1-day FC coverage need, minus available, inbound and on order — then rounded up to the case size and floored at the supplier minimum. Coverage can suggest units for a product with no recent sales when its rank is under the cutoff; see cov and coverage-bound/depth-bound below for which one drove this row.'}>
                                        Suggested Qty <SortIcon field="suggestedOrderQty" />
                                    </th>
                                    {advice && (
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                            title="Skip a product you have decided not to order, with a reason. It comes back if it gets more urgent, a new reason appears, stock falls 25%, or in 30 days.">
                                            Action
                                        </th>
                                    )}
                                    {/* Owner 2026-09-09: "consolidate four columns into one column
                                        with 4 rows inside wach row" and "the title is the column
                                        header". The labels live here once instead of on all 3,002
                                        rows; each stays its own sort target ("four header labels
                                        stay individually clickable as sort targets? yes"). */}
                                    {D(<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-10 bg-gray-50"
                                        title="Per-unit economics. MAPP and COGS come from the price list. FBA Fee and Ref. Fee are Amazon's own figures — from the Fee Preview report where it covers this ASIN, otherwise from Amazon's Product Fees API at MAP. Hover a row's fees for which basis applies. Click a label to sort by it.">
                                        <div className="flex flex-col gap-0.5">
                                            {[['mapPrice', 'MAPP'], ['priceListCost', 'COGS'],
                                              ['fbaFee', 'FBA Fee'], ['referralFee', 'Ref. Fee']].map(([field, label]) => (
                                                <span key={field}
                                                      data-testid={`econ-sort-${field}`}
                                                      onClick={() => handleSort(field)}
                                                      className="cursor-pointer hover:text-gray-700 whitespace-nowrap">
                                                    {label} <SortIcon field={field} />
                                                </span>
                                            ))}
                                        </div>
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('grossMarginPct')}
                                        title="Price − cost − FBA fee − referral fee, as % of that same price — the listing price where the Fee Preview report covers this ASIN, MAP (the floor) otherwise. Hover a row's value for its basis and the $ per unit.">
                                        Gross Margin <SortIcon field="grossMarginPct" />
                                    </th>)}
                                    {D(<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 sticky top-0 z-10 bg-gray-50" onClick={() => handleSort('estimatedCost')}
                                        title="Suggested quantity × unit cost.">
                                        Est. Cost <SortIcon field="estimatedCost" />
                                    </th>)}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredSuggestions.map((item, idx) => {
                                    const isSelected = !!selectedItems[item.asin];
                                    // Owner, 2026-09-08: "shade the rows that are cheated on for
                                    // more than 50% of the time". Since 2026-09-09 the same
                                    // editable threshold drives the shading and the exclusion, and
                                    // the count is server-fed on the row — so the column, the
                                    // shading and the filter can never disagree. Informs
                                    // purchasing; never filters demand.
                                    const cheatedDays = item.cheatedDays30;
                                    const cheatMajority = cheatDayThreshold != null
                                        && cheatedDays != null && cheatedDays >= cheatDayThreshold;
                                    return (
                                        <tr key={item.asin}
                                            title={cheatMajority ? `MAP-cheated ${cheatedDays} of the last 30 days` : undefined}
                                            className={isSelected ? 'bg-indigo-50' : cheatMajority ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}
                                        >
                                            <td className="px-3 py-3 text-right text-sm text-gray-400">{idx + 1}</td>
                                            <td className="px-3 py-3">
                                                {/* Minor fix (2): keyed on ASIN so a test can select this row's
                                                    checkbox directly instead of a positional index into
                                                    getAllByRole('checkbox'), which silently re-breaks every
                                                    time a checkbox is added to or removed from the filter bar. */}
                                                <input
                                                    type="checkbox"
                                                    data-testid={`row-select-${item.asin}`}
                                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                    checked={isSelected}
                                                    onChange={() => toggleItem(item.asin)}
                                                />
                                            </td>
                                            {/* Owner: "when you hover over the FBA inventory number - i want the
                                                map ... to appear" — the FC-map hover trigger and its popover moved
                                                to the FBA Avail number in the stock-cell below (2026-09-13). This
                                                cell keeps only the title, the Amazon link, and the ASIN/SKU/GTIN
                                                line. */}
                                            <td className="px-3 py-3">
                                                <div className="max-w-xs">
                                                    <p className="text-sm font-medium text-gray-900 whitespace-normal break-words"
                                                       title={item.productName}>
                                                        {item.productName}
                                                    </p>
                                                    {/* Owner 2026-09-11: link out to the detail page.
                                                        rel="noopener" is not decoration — without it
                                                        the opened tab can reach back through
                                                        window.opener and navigate this one. */}
                                                    <a data-testid={`amazon-link-${item.asin}`}
                                                       href={`https://www.amazon.com/dp/${item.asin}`}
                                                       target="_blank" rel="noopener noreferrer"
                                                       title={`Open ${item.asin} on Amazon`}
                                                       onClick={e => e.stopPropagation()}
                                                       className="text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap">
                                                        ↗ Amazon
                                                    </a>
                                                    <p className="text-xs text-gray-500">{item.asin} {item.sku ? `/ ${item.sku}` : ''} {item.gtin ? `/ ${item.gtin}` : ''}</p>
                                                </div>
                                            </td>
                                            {advice && (
                                                <td data-testid="why-cell" className="px-3 py-3 text-sm align-top">
                                                    {item.returnedFromSkip && (
                                                        <p data-testid="returned-from-skip" className="text-xs text-amber-700 mb-1">
                                                            {`Back — skipped ${item.returnedFromSkip.skippedAt} by ${item.returnedFromSkip.skippedBy}: "${item.returnedFromSkip.reasonText}" (${item.returnedFromSkip.cause})`}
                                                        </p>
                                                    )}
                                                    <ReasonChips reasons={item.reasons} />
                                                </td>
                                            )}
                                            {advice && (
                                                <td data-testid="order-by-cell"
                                                    className={`px-3 py-3 text-sm whitespace-nowrap ${item.bucket === 'ORDER_NOW'
                                                        ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                                                    {item.orderBy ?? '—'}
                                                </td>
                                            )}
                                            <td className="px-3 py-3">{stockStatusBadge(item.stockStatus)}</td>
                                            {D(<td className="px-3 py-3 text-right text-sm font-medium text-gray-900">{item.unitsSold30d}
                                                {item.mfnUnitsSold30d > 0 && <div className="text-xs text-gray-400" title="Merchant-fulfilled units (mostly returns) — not counted in velocity">+{item.mfnUnitsSold30d} MFN</div>}
                                            </td>)}
                                            {D(<td className="px-3 py-3 text-right text-sm text-gray-700">
                                                <div className="leading-tight">
                                                    <div>{item.dailyVelocity}</div>
                                                    {/* Stored monthly, shown per day so it sits directly
                                                        under our own figure and the gap needs no arithmetic.
                                                        Dash, never 0: an unscored listing is unmeasured, and
                                                        a 0 would argue against restocking. */}
                                                    <div data-testid="rank-vel-line"
                                                         className="text-xs text-gray-400"
                                                         title={item.rankImpliedUnitsMo == null
                                                             ? 'not scored — the rank curve covers currently-stocked ASINs only'
                                                             : `Listing-wide: ~${Math.round(item.rankImpliedUnitsMo)} units/month implied by rank. Upper bound.`}>
                                                        {item.rankImpliedUnitsMo == null
                                                            ? '—' : (item.rankImpliedUnitsMo / 30).toFixed(1)}
                                                    </div>
                                                </div>
                                            </td>)}
                                            {/* Task A, 2026-09-11: FBA Avail/Unsell./Used/Local SFP consolidated
                                                into one column (Unit Economics / MP precedent). ALWAYS four
                                                lines, in header order — a missing value keeps its own line as
                                                a dash so the values below never shift under the wrong header
                                                label.
                                                Reviewer, final-review fix wave: fbaAvailable is a nullable
                                                Double. `null < 10` is TRUE in JS, so a naive ternary would paint
                                                an unknown value orange (a false low-stock warning); guard
                                                explicitly on `== null` first. Local SFP reads the enrichment
                                                envelope directly (localSfpUnits is undefined both when the row
                                                is past the 500-row cap and when it's enriched with a genuine
                                                null) — `dash()` treats undefined and null the same way, so
                                                both cases correctly render a dash.
                                                Owner, verbatim (2026-09-13): "when you hover over the FBA inventory
                                                number - i want the map of the continental US to appear and show
                                                the FCs that contain the units." The FC map (already built for the
                                                title cell) now triggers off the FBA Avail number specifically —
                                                the other three lines carry no affordance. hasMap stays the gate: a
                                                row past the 500-row enrichment cap (or genuinely un-enriched) has
                                                no fcs, so it gets its honest number with no dotted underline and
                                                no popover — never an empty map or a fabricated zero.
                                                Enter on the number; leave on the CELL — the popover is a DOM
                                                child, so moving the cursor into the map keeps it open (same
                                                pattern as BuildPoModal). The popover right-aligns to the cell
                                                (`right-0`, not `left-0`): this column sits mid-table, and a
                                                left-anchored 512px popover from here would run off the right edge
                                                of the viewport / force horizontal scroll on the table. Anchoring
                                                right and growing leftward keeps it inside the columns already
                                                rendered to the left (title, status, sold, velocity — comfortably
                                                more than 512px). */}
                                            {D(<td data-testid="stock-cell" className="px-3 py-3 text-right text-sm whitespace-nowrap relative"
                                                onMouseLeave={() => setHoverAsin(prev => (prev === item.asin ? null : prev))}>
                                                {(() => {
                                                    const e = enrichment[item.asin];
                                                    const { pins, unknown } = fcPins(e?.fcs);
                                                    const hasMap = pins.length > 0 || unknown.length > 0;
                                                    return (
                                                        <div className="flex flex-col items-end gap-0.5">
                                                            {[
                                                                { v: item.fbaAvailable, cls: `font-medium ${
                                                                    item.fbaAvailable == null ? 'text-gray-400' :
                                                                    item.fbaAvailable === 0 ? 'text-red-600' :
                                                                    item.fbaAvailable < 10 ? 'text-orange-600' : 'text-gray-900'
                                                                }${hasMap ? ' underline decoration-dotted cursor-help' : ''}`,
                                                                  isFba: true },
                                                                { v: item.fbaUnsellable, cls: 'text-gray-700' },
                                                                { v: item.fbaUsedSellable, cls: 'text-gray-700' },
                                                                { v: enrichment[item.asin]?.localSfpUnits, cls: 'text-gray-700' },
                                                            ].map((line, i) => (
                                                                <span key={i} data-testid="stock-line" className={line.cls}
                                                                      onMouseEnter={line.isFba ? () => hasMap && setHoverAsin(item.asin) : undefined}>
                                                                    {dash(line.v, String)}
                                                                </span>
                                                            ))}
                                                            {/* Owner 2026-10-25: warehouses holding stock, reserved units in parentheses. The FC
                                                                count is the warehouse ledger (2 days behind); reserved is product-wide (Amazon
                                                                does not say which warehouse). Nothing buyable but reserved units → say so. */}
                                                            {item.fcsHolding != null && (item.fcsHolding > 0 || item.fbaReserved > 0) && (
                                                                <span data-testid="stock-fcs" className="text-xs text-gray-500"
                                                                      title="Warehouses with sellable stock in Amazon's inventory ledger (about 2 days behind). Reserved = units Amazon holds back for pending orders or processing; Amazon reports these per product, not per warehouse.">
                                                                    {item.fbaAvailable === 0 && !(item.fbaTransfer > 0)
                                                                        ? 'No buyable stock'
                                                                        : (stockSpreadText(item.spread)
                                                                            || `In ${item.fcsHolding} FC${item.fcsHolding === 1 ? '' : 's'}`)}
                                                                    {item.fbaReserved > 0 ? ` (${item.fbaReserved} reserved)` : ''}
                                                                </span>
                                                            )}
                                                            {/* 2026-10-25: units Amazon is moving between its own warehouses — ours, counted as stock. */}
                                                            {item.fbaTransfer > 0 && (
                                                                <span data-testid="stock-transfer" className="text-xs text-blue-700"
                                                                      title="Units Amazon is moving between its own warehouses (Manage FBA Inventory report). Not fulfillable yet, but counted as stock for reordering.">
                                                                    +{item.fbaTransfer} moving between FCs
                                                                </span>
                                                            )}
                                                            {hoverAsin === item.asin && hasMap && (
                                                                <div className="absolute right-0 top-full z-30 w-[32rem] shadow-xl border border-gray-200 rounded-md">
                                                                    <FcPinMap pins={pins} unknown={unknown} heightPx={200}
                                                                              selectedLabel="Selected plans" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>)}
                                            {/* Task 2, 2026-09-10: ALWAYS two lines, in header order. A missing
                                                value keeps its own line as a dash — same alignment invariant as
                                                unit-econ-cell. Unknown (never probed) is colour-keyed off `== null`,
                                                not `=== 0` — a genuinely probed zero is real information, not an
                                                absent one, and must not look muted.
                                                Owner, verbatim: "when hovering over the MPs invenrntoy data the
                                                tool tip should indicate the date it was checked". mpCheckedAt is
                                                populated by the server even when mpInventory dashes for staleness
                                                (past the 48h bound) — so this title distinguishes "checked
                                                2026-06-13" (probed but stale) from no title at all (never probed),
                                                the same distinguishing role mapWatchState plays for the Cheat·30d
                                                dash. */}
                                            {/* Owner, 2026-09-11: "color code MP Inv column to a light red" — a
                                                pale background TINT (bg-red-50), text colours below are
                                                untouched (text-purple-700 / text-gray-400 still stand). */}
                                            {D(<td data-testid="mp-cell" className="px-3 py-3 text-right text-sm whitespace-nowrap bg-red-50"
                                                title={item.mpCheckedAt ? `Marketplace Pros inventory checked ${item.mpCheckedAt}` : undefined}>
                                                <div className="flex flex-col items-end gap-0.5">
                                                    {[
                                                        { v: item.mpInventory, fmt: String,
                                                          cls: item.mpInventory == null ? 'text-gray-400 font-medium' : 'text-purple-700 font-medium' },
                                                        { v: enrichment[item.asin]?.mpVel7d, fmt: (x) => x.toFixed(1),
                                                          cls: 'text-purple-700' },
                                                    ].map((line, i) => (
                                                        <span key={i} data-testid="mp-line" className={line.cls}>
                                                            {dash(line.v, line.fmt)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>)}
                                            {D(<td className="px-3 py-3 text-right text-sm text-gray-700" title={INBOUND_SOURCE_TITLE[item.inboundSource]}>{dash(item.fbaInbound, String)}</td>)}
                                            {/* Owner, 2026-09-07: hovering on-order names the POs behind the number. */}
                                            {D(<td className="px-3 py-3 text-right text-sm text-gray-700"
                                                title={item.onOrderPos ? `Open PO(s): ${item.onOrderPos}` : undefined}>
                                                {item.onOrder > 0 ? item.onOrder.toLocaleString() : '-'}
                                                {item.sfpOnOrder > 0 && (
                                                    <div className="text-[10px] text-teal-700 whitespace-nowrap"
                                                         title="Units on order for SFP (warehouse) — not counted in On Order">
                                                        +{item.sfpOnOrder} SFP
                                                    </div>
                                                )}
                                            </td>)}
                                            {D(<td data-testid="here-cell" className="px-3 py-3 text-right text-sm">
                                                {item.hereForFba == null ? '—'
                                                    : item.hereForFba > 0
                                                        ? <a className="text-indigo-600 hover:underline" href={`/shipment-planner-v2?po=${encodeURIComponent(item.herePo || '')}`}>{item.hereForFba}</a>
                                                        : '-'}
                                            </td>)}
                                            {/* Reviewer, final-review fix wave: daysOfSupply is now a nullable Double
                                                (no velocity, or unknown stock). `null < 14` is TRUE in JS, so the old
                                                ternary painted every unknown-DOS row urgent red — a catalog-wide false
                                                emergency. Unknown must dash in neutral tone, never red or orange. */}
                                            {D(<td className={`px-3 py-3 text-right text-sm font-medium ${
                                                item.daysOfSupply == null ? 'text-gray-400' :
                                                item.daysOfSupply < 14 ? 'text-red-600' :
                                                item.daysOfSupply < 30 ? 'text-orange-600' : 'text-gray-900'
                                            }`}>
                                                {dash(item.daysOfSupply, String)}
                                            </td>)}
                                            {(() => {
                                                const e = enrichment[item.asin];
                                                return D(
                                                    <>
                                                        {/* Local SFP moved into the consolidated stock-cell above
                                                            (Task A, 2026-09-11). */}
                                                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                                                            <div className="leading-tight">
                                                                <div>{fmtPromise(e)}</div>
                                                                {/* Owner 2026-09-11: the current average
                                                                    promise. The rival's sits on hover, not
                                                                    on the row — buy-box wins track RELATIVE
                                                                    speed, so ours alone cannot say whether
                                                                    we are winning, but two numbers per line
                                                                    would crowd the column. */}
                                                                <div data-testid="promise-line"
                                                                     className="text-xs text-gray-400"
                                                                     title={item.ourPromiseDays == null
                                                                         ? 'no current promise measured for this ASIN'
                                                                         : `Our average promise ${item.ourPromiseDays}d`
                                                                           + (item.mpPromiseDays == null
                                                                               ? ' — Marketplace Pros not measured'
                                                                               : ` vs Marketplace Pros ${item.mpPromiseDays}d`)}>
                                                                    {item.ourPromiseDays == null
                                                                        ? '—' : `${item.ourPromiseDays.toFixed(1)}d`}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        {/* Task 6: three stacked lenses, aligned by position — a missing lens still
                                                            occupies its line as a dash so the values below never shift under the
                                                            wrong label. */}
                                                        <td className="px-3 py-3 text-right text-sm text-gray-700">
                                                            <div className="leading-tight">
                                                                <div>{fmtPct(item.cov1dReachPct)}</div>
                                                                <div className="text-gray-500">{fmtPct(item.cov1dDemandPct)}</div>
                                                                <div className="text-gray-500">{fmtPct(item.cov1dEffectivePct)}</div>
                                                            </div>
                                                        </td>
                                                        {(() => {
                                                            const sc = spreadCell(item.spread);
                                                            const lvl = warningLevel(item.spread);
                                                            return (
                                                                <td data-testid="spread-cell" title={sc.title}
                                                                    className={`px-3 py-3 text-left text-sm whitespace-nowrap cursor-help ${sc.sort === -1 && sc.text === '—' ? 'text-gray-400' : 'text-indigo-700 dark:text-indigo-300'}`}>
                                                                    <div>
                                                                        {lvl && (
                                                                            <span data-testid="spread-warning-marker"
                                                                                  className={`${lvl === 'LOW' ? 'text-amber-600' : 'text-red-600'} mr-1`}
                                                                                  title={warningText(item.spread)}>●</span>
                                                                        )}
                                                                        {sc.text}
                                                                    </div>
                                                                    {lvl && <div className="text-xs text-gray-600 dark:text-gray-400">{warningText(item.spread)}</div>}
                                                                    {coverageBreakdown(item.spread) && (
                                                                        <div data-testid="coverage-breakdown" className="text-xs text-gray-600 dark:text-gray-400 whitespace-normal min-w-[14rem]">
                                                                            {coverageBreakdown(item.spread)}
                                                                        </div>
                                                                    )}
                                                                    {(lvl === 'CRITICAL' || lvl === 'OUT') && (
                                                                        <a className="text-xs text-blue-600 underline" href={`/shipment-planner-v2?refill=${item.asin}`}>Refill now</a>
                                                                    )}
                                                                </td>
                                                            );
                                                        })()}
                                                        {/* BB Win Rate. Dash = outside the scorecard's
                                                            stocked-ASIN universe, i.e. unmeasured — a 0
                                                            here would read as "we never win the box" and
                                                            argue for restocking exactly what we should
                                                            not. A measured 0 still renders 0. */}
                                                        <td data-testid="bb-win-cell"
                                                            className={`px-3 py-3 text-right text-sm ${
                                                                item.bbWinPct == null ? 'text-gray-400'
                                                                    : item.bbWinPct < 30 ? 'text-red-700' : 'text-gray-700'}`}
                                                            title={item.bbWinPct == null
                                                                ? 'not scored — only currently-stocked ASINs are measured'
                                                                : `${item.bbWinPct}% of the last 28 days' page views saw us in the Buy Box`}>
                                                            {fmtPct(item.bbWinPct)}
                                                        </td>
                                                        {/* BB Holder (owner brief 2026-09-13, corrected same
                                                            day): who holds it RIGHT NOW, next to BB Win's
                                                            28-day rate but a different question. See
                                                            bbHolderCellContent / bbHolderTitle above — there
                                                            is no "nobody holds it" state; an undisclosed
                                                            holder is unknown, same as stale or never
                                                            checked, distinguished only in the tooltip. */}
                                                        <td data-testid="bb-holder-cell"
                                                            className="px-3 py-3 text-center text-sm whitespace-nowrap"
                                                            title={bbHolderTitle(item)}>
                                                            {bbHolderCellContent(item)}
                                                        </td>
                                                        {(() => {
                                                            // Owner 2026-09-10: a zero must say why it is a
                                                            // zero. Wording for the no-offers case is
                                                            // exactly "no buyable offers" — no elaboration.
                                                            const st = item.mapWatchState;
                                                            const title = st === 'NO_BUYABLE_OFFERS' ? 'no buyable offers'
                                                                : st === 'STALE' ? 'offer feed is stale — MAP data cannot be checked'
                                                                : st == null ? 'not monitored for MAP violations'
                                                                : undefined;
                                                            const showDash = st !== 'WATCHED' || item.cheatedDays30 == null;
                                                            return (
                                                                <td data-testid="cheat-cell" title={title}
                                                                    className={`px-3 py-3 text-right text-sm ${
                                                                        !showDash && item.cheatedDays30 > 0
                                                                            ? 'text-amber-700' : 'text-gray-500'}`}>
                                                                    {showDash ? '—' : String(item.cheatedDays30)}
                                                                </td>
                                                            );
                                                        })()}
                                                        {/* Server-resolved rank (rides the suggestion row) — the SAME number the
                                                            rank-cutoff filter reads, so filter and column can never disagree. */}
                                                        <td className="px-3 py-3 text-right text-sm text-gray-500">{fmtRank(item.salesRank)}</td>
                                                    </>
                                                );
                                            })()}
                                            {/* Box configuration cell. Unknown is never zero and never a
                                                plausible default (owner: a fabricated 1 looks correct, never
                                                prompts an edit, and can under-order coverage by the true pack
                                                multiple). unitsPerBox <= 0 (including the legacy 0 rows and
                                                null) always renders the actionable affordance, never a number. */}
                                            {D(<td data-testid="box-cell" className="px-3 py-3 text-right text-sm whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    data-testid={`box-edit-${item.asin}`}
                                                    onClick={() => openBoxModal(item)}
                                                    disabled={boxModalLoadingAsin === item.asin}
                                                    title={boxModalLoadingAsin === item.asin
                                                        ? 'Loading the current packaging…'
                                                        : (item.unitsPerBox > 0
                                                            ? `Edit box configuration — ${item.unitsPerBox}/box`
                                                            : 'No packaging default yet — click to set units per box so coverage can size a quantity for this product')}
                                                    className={`disabled:opacity-50 disabled:cursor-wait ${item.unitsPerBox > 0
                                                        ? 'text-gray-900 hover:text-indigo-600 underline decoration-dotted'
                                                        : 'text-indigo-600 hover:text-indigo-800 underline decoration-dashed font-medium'}`}
                                                >
                                                    {boxModalLoadingAsin === item.asin
                                                        ? 'Loading…'
                                                        : (item.unitsPerBox > 0 ? `${item.unitsPerBox}/box` : 'Set pack')}
                                                </button>
                                            </td>)}
                                            <td className="px-3 py-3 text-right">
                                                {/* Owner 2026-09-09: "add a input field for order qty" — typing here
                                                    selects the row. The placeholder shows the suggestion without
                                                    committing it, so an untouched row reads as "not ordered" rather
                                                    than "ordered the suggested amount". */}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    data-testid="row-qty-input"
                                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                                    value={selectedItems[item.asin]?.quantity ?? ''}
                                                    placeholder={item.suggestedOrderQty ?? ''}
                                                    onChange={e => updateDraftQty(item.asin, e.target.value)}
                                                    aria-label={`order quantity for ${item.productName}`}
                                                />
                                                {item.boxMissing && (
                                                    <div data-testid={`box-missing-${item.asin}`}
                                                        className="mt-1 text-xs text-amber-700 dark:text-amber-400"
                                                        title="No shipping box set — this quantity is not rounded to a box">Box size missing</div>
                                                )}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    data-testid={`row-sfp-input-${item.asin}`}
                                                    className="mt-1 w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                                    value={selectedItems[item.asin]?.sfpQuantity || ''}
                                                    placeholder="SFP"
                                                    onChange={e => updateDraftSfp(item.asin, e.target.value)}
                                                    aria-label={`units for SFP for ${item.productName}`}
                                                    title="Units kept in the warehouse for SFP; the rest go to FBA"
                                                />
                                                {/* Task 4: bindingConstraint states WHY the suggestion above is what it
                                                    is — COVERAGE means the ≤1-day FC coverage target drove the number,
                                                    DEPTH means the days-of-supply target did. null (no evaluation) shows
                                                    neither marker. coverageUnits is the FC-holdings-derived figure behind
                                                    that decision; it is null far more often than not (only 54 of 2,900
                                                    catalog ASINs carry a usable pack size) and dashes deliberately —
                                                    never hiding or replacing the depth suggestion in the input above. */}
                                                <div className="text-xs text-gray-400 mt-1 whitespace-nowrap">
                                                    {!advice && item.bindingConstraint === 'COVERAGE' && (
                                                        <span data-testid="binding-constraint"
                                                            title="This quantity is bound by ≤1-day FC coverage, not depth of supply.">
                                                            coverage-bound
                                                        </span>
                                                    )}
                                                    {!advice && item.bindingConstraint === 'DEPTH' && (
                                                        <span data-testid="binding-constraint"
                                                            title="This quantity is bound by depth of supply, not ≤1-day FC coverage.">
                                                            depth-bound
                                                        </span>
                                                    )}
                                                    {spreadBreakdown(item) && (
                                                        <span data-testid="spread-breakdown" className="block"
                                                              title="Suggested units split: depth of supply plus the spread units added to seed the missing FC regions (net of spare units already on order).">
                                                            {item.bindingConstraint === 'SPREAD' && !advice && <span data-testid="binding-constraint">spread-bound · </span>}
                                                            depth {spreadBreakdown(item).depth} + spread {spreadBreakdown(item).spread}
                                                        </span>
                                                    )}
                                                    {/* Spec 2026-11-03 §7: the coverage size beside today's own-sales
                                                        formula, so the jump is visible. Advised rows only carry sizedBy;
                                                        bindingConstraint COVERAGE there means "sized for coverage", unlike
                                                        the legacy ≤1-day marker above. */}
                                                    {(item.sizedBy === 'COVERAGE' || item.sizedBy === 'NATIONAL') && item.ownSalesQty != null && (() => {
                                                        const delta = (Number(item.suggestedOrderQty) || 0) - item.ownSalesQty;
                                                        return (
                                                            <span data-testid="coverage-vs-own" className="block"
                                                                  title="Coverage: the national rank-implied sales rate × each region's population weight × the horizon, minus stock held there, boxed per region. Own sales: today's formula — our own 30-day sales rate × days of supply, minus stock and on order.">
                                                                coverage {item.suggestedOrderQty ?? 0} vs own sales {item.ownSalesQty} ({delta >= 0 ? '+' : ''}{delta})
                                                            </span>
                                                        );
                                                    })()}
                                                    {item.sizedBy === 'OWN_SALES' && (
                                                        <span data-testid="coverage-vs-own" className="block"
                                                              title="No usable sales rank, or the Buy Box is lost on price: sized from our own sales rate, as before.">
                                                            sized by own sales
                                                        </span>
                                                    )}
                                                    {item.overstock && item.coverDaysTotal != null && (
                                                        <span data-testid="cover-days" className="block text-amber-700 dark:text-amber-400"
                                                              title="Days of cover across all regions at the sizing rate">
                                                            {Math.round(item.coverDaysTotal)} d cover
                                                        </span>
                                                    )}
                                                    <span data-testid="coverage-units"
                                                        title={`GROSS ≤1-day FC coverage shortfall at the ${coverageTargetPct}% target — FCs not yet holding this ASIN × pack size, BEFORE deducting available, inbound and on order. The suggested quantity above is the NET figure, after those deductions: a positive cov beside a suggested quantity of 0 means stock on hand already covers the gap. Dash means pack size or FC holdings are unknown, not zero.`}>
                                                        {' '}cov {item.coverageUnits != null ? item.coverageUnits : '—'}
                                                    </span>
                                                </div>
                                            </td>
                                            {advice && (
                                                <td data-testid="action-cell" className="px-3 py-3 text-sm whitespace-nowrap">
                                                    {item.skip ? (
                                                        <div className="max-w-[14rem] whitespace-normal">
                                                            <button type="button" data-testid={`unskip-${item.asin}`}
                                                                    onClick={() => unskip(item.asin)}
                                                                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                                                                Un-skip
                                                            </button>
                                                            <p className="text-xs text-gray-500 mt-1">{item.skip.reasonText}</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                        {item.bucket === 'SHIP' && (
                                                            <a data-testid={`ship-${item.asin}`} href={`/shipment-planner-v2?refill=${encodeURIComponent(item.asin)}`}
                                                               className="mr-2 text-xs font-semibold text-indigo-600 hover:underline">Ship in Planner V2</a>
                                                        )}
                                                        <button type="button" data-testid={`skip-${item.asin}`}
                                                                onClick={() => setSkipRow(item)}
                                                                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                                                            Skip
                                                        </button>
                                                        </>
                                                    )}
                                                </td>
                                            )}
                                            {/* ALWAYS four lines, in header order. A missing
                                                value keeps its own line as a dash — if it
                                                collapsed, every value below would shift up and
                                                sit under the wrong header label, silently
                                                misreporting one figure as another.

                                                Owner 2026-09-10: feeBasis names which price the FBA
                                                and referral fees were computed at. Both sources are
                                                Amazon's own arithmetic — never "estimated" — so the
                                                tooltip states the question answered (fees at MAP vs
                                                fees at our listing price), not a confidence level. */}
                                            {D(<td data-testid="unit-econ-cell"
                                                title={item.feeBasis === 'MAP'
                                                    ? 'FBA and referral fees as Amazon computes them at MAP'
                                                    : item.feeBasis === 'LISTING_PRICE'
                                                        ? 'FBA and referral fees as Amazon computes them at your current listing price'
                                                        : undefined}
                                                className="px-3 py-3 text-right text-sm text-gray-700 whitespace-nowrap">
                                                <div className="flex flex-col gap-0.5">
                                                    {[item.mapPrice, item.priceListCost,
                                                      item.fbaFee, item.referralFee].map((v, i) => (
                                                        <span key={i} data-testid="unit-econ-line">
                                                            {v != null ? `$${v.toFixed(2)}` : '—'}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>)}
                                            {/* Important fix (I2): Gross Margin is now heterogeneous — ~215 rows
                                                measured at the listing price, the rest at MAP (systematically
                                                lower, since MAP is the floor) — and the basis otherwise lives only
                                                on the unit-econ-cell two columns over. Same wording as that cell's
                                                title so a row's margin is self-explanatory where it is read.
                                                Presentation only: no threshold, sort, or margin maths changed. */}
                                            {D(<td title={item.feeBasis === 'MAP'
                                                    ? 'Margin at MAP — FBA and referral fees as Amazon computes them at MAP'
                                                    : item.feeBasis === 'LISTING_PRICE'
                                                        ? 'Margin at your current listing price — FBA and referral fees as Amazon computes them at that price'
                                                        : undefined}
                                                className={`px-3 py-3 text-right text-sm font-medium ${
                                                item.grossMarginPct == null ? 'text-gray-400' :
                                                item.grossMarginPct < 0 ? 'text-red-600' :
                                                item.grossMarginPct < 15 ? 'text-orange-600' :
                                                'text-green-700'
                                            }`}>
                                                {/* Minor 10 fix: the colour guard already keyed off grossMarginPct
                                                    while the content guard keyed off grossMargin — grossMargin is
                                                    set whenever the four inputs are known, but grossMarginPct only
                                                    when sellingPrice > 0, so a zero-price row would call
                                                    .toFixed(1) on null and throw. Guard on grossMarginPct in both
                                                    places. */}
                                                {item.grossMarginPct != null ? (
                                                    <span title={item.grossMargin != null ? `$${item.grossMargin.toFixed(2)} per unit` : undefined}>
                                                        {item.grossMarginPct.toFixed(1)}%
                                                    </span>
                                                ) : '-'}
                                            </td>)}
                                            {(() => {
                                                // Owner 2026-09-09: Est. Cost follows the
                                                // quantity you entered. suggestedOrderQty may be
                                                // null (we could not suggest one) — a typed
                                                // quantity is still computable. Unknown unit
                                                // cost dashes; it never fabricates $0. Same
                                                // helper the estimatedCost sort accessor uses
                                                // (reviewer, final fix wave) — one formula, so
                                                // display and sort order can't diverge.
                                                const cost = effectiveCost(item, selectedItems);
                                                return D(
                                                    <td data-testid="est-cost-cell"
                                                        className="px-3 py-3 text-right text-sm text-gray-700">
                                                        {cost != null
                                                            ? `$${cost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                                                            : '-'}
                                                    </td>
                                                );
                                            })()}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredSuggestions.length === 0 && !loading && advice && bucketCounts[bucketTab].count === 0 && (
                            <div className="text-center py-12 text-gray-500">
                                Nothing in {BUCKETS.find(b => b.key === bucketTab)?.label} right now
                            </div>
                        )}
                        {filteredSuggestions.length === 0 && !loading && !(advice && bucketCounts[bucketTab].count === 0) && (
                            <div className="text-center py-12 text-gray-500">No products match the current filters</div>
                        )}
                    </div>
                )}
            </div>

            <SpreadSettingsModal open={spreadSettingsOpen} onClose={() => setSpreadSettingsOpen(false)} onSaved={() => fetchSuggestions()} />
            <ReorderSettingsModal open={reorderSettingsOpen} onClose={() => setReorderSettingsOpen(false)} onSaved={() => { loadReorderSettings(); fetchSuggestions(); }} />

            {skipRow && (
                <SkipModal asin={skipRow.asin} bucket={skipRow.bucket} reasons={skipRow.reasons}
                           fbaAvailable={skipRow.fbaAvailable}
                           onClose={() => setSkipRow(null)}
                           onSaved={() => { removeFromDraft(skipRow.asin); setSkipRow(null); fetchSuggestions(); }} />
            )}

            {createdPo && draftItems.length === 0 && (
                <div data-testid="po-created-banner" className="mb-4 rounded-md bg-green-50 dark:bg-green-900/30 px-4 py-2 text-sm text-green-800 dark:text-green-200">
                    PO #{createdPo} created.{' '}
                    <a className="underline text-blue-600" href={`/shipment-planner-v2?po=${createdPo}`}>Open in Planner V2</a>
                </div>
            )}
            {/* PO Draft Panel */}
            {draftItems.length > 0 && (
                <div className="bg-white rounded-lg shadow sticky bottom-0 z-10 border-t-4 border-indigo-600">
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">PO Draft</h3>
                                <p className="text-sm text-gray-500" data-testid="draft-summary">
                                    {draftItems.length} items, {draftTotalUnits.toLocaleString()} units, ${draftTotalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    {draftCostExcluded > 0 && ` (${draftCostExcluded} excluded — unknown cost)`}
                                </p>
                                {draftTotalSfp > 0 && (
                                    <p className="text-sm text-teal-700" data-testid="draft-sfp-summary">
                                        SFP {draftTotalSfp} · FBA {draftTotalUnits - draftTotalSfp}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-medium text-gray-700">PO #:</label>
                                    <input
                                        type="text"
                                        value={poNumber}
                                        onChange={e => setPoNumber(e.target.value)}
                                        className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                    />
                                </div>
                                <button
                                    onClick={clearDraft}
                                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    Clear
                                </button>
                                <button
                                    onClick={handleCreatePO}
                                    disabled={creating}
                                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                                >
                                    {creating ? 'Creating...' : 'Create PO'}
                                </button>
                            </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ASIN</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Unit Cost</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Qty</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">For SFP</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500" title="Units of this line meant to seed missing FC regions">Spread</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Line Total</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {draftItems.map(item => (
                                        <tr key={item.asin}>
                                            <td className="px-3 py-2 text-sm text-gray-900 max-w-xs whitespace-normal break-words">
                                                {item.productName}
                                                {/* Owner 2026-09-09: typing a quantity now seeds a draft line for a row
                                                    that was never ticked — show the SKU here so the line's identity is
                                                    visibly carried, not just its quantity. */}
                                                {item.sku && <div className="text-xs text-gray-500">{item.sku}</div>}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-gray-500">{item.asin}</td>
                                            <td className="px-3 py-2 text-right text-sm text-gray-700">
                                                {item.unitCost != null ? `$${item.unitCost.toFixed(2)}` : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={e => updateDraftQty(item.asin, e.target.value)}
                                                    className="w-20 text-right rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={item.quantity}
                                                    data-testid={`draft-sfp-input-${item.asin}`}
                                                    value={item.sfpQuantity || 0}
                                                    onChange={e => updateDraftSfp(item.asin, e.target.value)}
                                                    className="w-20 text-right rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                />
                                                <div className="text-[10px] text-gray-500">FBA {item.quantity - (item.sfpQuantity || 0)}</div>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={Math.max(0, item.quantity - (item.sfpQuantity || 0))}
                                                    data-testid={`draft-spread-input-${item.asin}`}
                                                    value={item.spreadQuantity || 0}
                                                    onChange={e => updateDraftSpread(item.asin, e.target.value)}
                                                    className="w-20 text-right rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">
                                                {item.unitCost != null ? `$${(item.unitCost * item.quantity).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    onClick={() => removeFromDraft(item.asin)}
                                                    className="text-red-500 hover:text-red-700 text-sm"
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Box configuration editor (Task 3): the SAME modal + hook the shipment planner
                uses -- see the useBoxConfig wiring above for requirePersist (Finding 1: the
                "make default" POST here is the entire operation, so it's awaited before the
                editor ever applies/closes) and primaryActionOnly below (Finding 2: there is no
                shipment on this page, so only the persisting action is shown). */}
            <div ref={boxModalWrapperRef}>
                <BoxConfigModal
                    packagingPopoverAsin={packagingPopoverAsin}
                    packagingPopoverTitle={packagingPopoverTitle}
                    packagingPopoverQty={packagingPopoverQty}
                    packagingForm={packagingForm}
                    packagingFieldErrors={packagingFieldErrors}
                    packagingFormValid={packagingFormValid}
                    packagingAttemptedSubmit={packagingAttemptedSubmit}
                    packagingStyleChoice={packagingStyleChoice}
                    packagingSaving={packagingSaving}
                    packagingLoadedLbs={packagingLoadedLbs}
                    packagingSummaryLine={packagingSummaryLine}
                    packagingModalActivePkg={packagingModalActivePkg}
                    packagingModalDerived={packagingModalDerived}
                    packagingModalLegacyOnly={packagingModalLegacyOnly}
                    packagingModalTotals={packagingModalTotals}
                    boxCatalog={boxCatalog}
                    choosePackagingStyle={choosePackagingStyle}
                    closePackagingPopover={closePackagingPopover}
                    applyPackaging={applyPackaging}
                    updatePackagingField={updatePackagingField}
                    fmtLbsShort={fmtLbsShort}
                    newBoxOpen={newBoxOpen}
                    newBoxForm={newBoxForm}
                    newBoxSaving={newBoxSaving}
                    editingBoxId={editingBoxId}
                    boxError={boxError}
                    boxNote={boxNote}
                    selectedBox={selectedBox}
                    updateNewBoxField={updateNewBoxField}
                    saveNewBoxToCatalog={saveNewBoxToCatalog}
                    renderCatalogBoxCard={renderCatalogBoxCard}
                    setEditingBoxId={setEditingBoxId}
                    setNewBoxForm={setNewBoxForm}
                    setBoxError={setBoxError}
                    setNewBoxOpen={setNewBoxOpen}
                    setSelectedBoxId={setSelectedBoxId}
                    primaryActionOnly
                    // Owner decision (task 3): a BOTH-designated ASIN gets an explicit UPS/LTL
                    // picker instead of the old silent 500 lb LTL default. boxModalShipmentMode
                    // IS the choice (and the useBoxConfig shipmentMode input above) -- picking
                    // just calls its setter directly, no separate piece of state needed.
                    shipmentModePickerVisible={boxModalIsBothDesignated}
                    shipmentModeChoice={boxModalShipmentMode}
                    onChooseShipmentMode={setBoxModalShipmentMode}
                />
                {/* BoxConfigModal has no slot for a save-failure message that isn't box-catalog
                    CRUD (boxError) -- this page's own failure surface, fixed so it's visible
                    regardless of which packaging style (UNIT/CARTON) is open underneath it.
                    Finding B fix: ref'd so the outside-click handler above can protect it -- a
                    click here is never mistaken for a dismiss-and-discard gesture. */}
                {saveError && packagingPopoverAsin && (
                    <div ref={boxSaveErrorRef} data-testid="box-save-error"
                         className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-md rounded-md bg-red-600 px-4 py-2 text-sm text-white shadow-lg">
                        {saveError}
                    </div>
                )}
            </div>
        </div>
    );
}
